import { useState, useEffect, useCallback, useRef, useMemo, Fragment } from 'react'
import { useDropzone } from 'react-dropzone'
import { pdfjs } from 'react-pdf'
import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  useMapEvents,
} from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
// Bundle the pdf.js worker and Leaflet's marker images instead of pulling them
// from unpkg at runtime (CSP lane, 2026-09-01): a marketing-page that executes
// worker SCRIPT from a third-party CDN is exactly what the www CSP's
// `script-src 'self'` + `worker-src 'self'` exist to refuse, and
// scripts/assert-external-origins.mjs now fails the build if unpkg reappears.
// `?url` emits the worker as a same-origin asset; the version can never drift
// from `pdfjs.version` because it is the same installed package.
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url'
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl

delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
})

// ─── Types ──────────────────────────────────────────────────────────────────

type RoutePoint = { id: string; pitId: string; lat: number; lng: number }

function nextPitId(last: string | undefined): string {
  if (!last) return ''
  const m = last.match(/^(.*?)(\d+)$/)
  if (!m) return ''
  return m[1] + String(parseInt(m[2], 10) + 1).padStart(m[2].length, '0')
}

type PdfPoint = { x: number; y: number }
type ControlPoint = { id: string; pdf: PdfPoint; lat: number; lng: number }

type Affine = { a: number; b: number; c: number; d: number; e: number; f: number }
// X = a*x + b*y + c ; Y = d*x + e*y + f

type TextItemPos = { text: string; x: number; y: number; width: number; height: number }

function haversineM(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLng = (lng2 - lng1) * Math.PI / 180
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(a))
}

type SheetSettings = {
  opacity: number
  pdfScale: number
  rotation: number
  locked: boolean
  settingsOpen: boolean
  /** Transform captured from the screen-space imgBox at the moment of locking. */
  lockedTransform?: Affine | null
  /** Sheet role — determines ID assignment and default status. */
  type: 'Map' | 'Overview' | 'Title'
  /** True when position was computed automatically from the overview, not manually locked. */
  autoApprox?: boolean
  /** True when auto-approx was attempted but the sheet's label could not be found on the overview. */
  autoApproxFailed?: boolean
}
const DEFAULT_SHEET: SheetSettings = {
  opacity: 0.55,
  pdfScale: 100,
  rotation: 0,
  locked: false,
  settingsOpen: false,
  type: 'Map',
}

type PageData = {
  dataUrl: string
  widthPx: number
  heightPx: number
  /** Physical page width in millimeters (from PDF user units, 1pt = 0.3528mm) */
  widthMm: number
  /** Physical page height in millimeters */
  heightMm: number
  numPages: number
  text: string
  /** Map scale denominator from the title block (e.g. 1000 for "1:1000"), or null */
  mapScale: number | null
  textItems: TextItemPos[]
}

// ─── PDF helpers ─────────────────────────────────────────────────────────────

async function loadPdf(file: File) {
  const buf = await file.arrayBuffer()
  return pdfjs.getDocument({ data: buf }).promise
}

async function renderPage(pdf: pdfjs.PDFDocumentProxy, pageNumber: number): Promise<PageData> {
  const page = await pdf.getPage(pageNumber)
  // Physical page size: PDF user units → mm (1 pt = 1/72 inch = 0.3528 mm)
  const baseViewport = page.getViewport({ scale: 1 })
  const widthMm = baseViewport.width * 0.3528
  const heightMm = baseViewport.height * 0.3528
  // scale 1.5 → ~44% fewer pixels than scale 2; stored as JPEG for ~20–30× less memory
  // vs PNG. For a geo-referenced overlay this is more than sufficient quality.
  const RENDER_SCALE = 1.5
  const viewport = page.getViewport({ scale: RENDER_SCALE })
  const canvas = document.createElement('canvas')
  canvas.width = viewport.width
  canvas.height = viewport.height
  const ctx = canvas.getContext('2d')!
  await page.render({ canvasContext: ctx, viewport, canvas }).promise
  const textContent = await page.getTextContent()
  const text = textContent.items
    .map((i) => ('str' in i ? i.str : ''))
    .join(' ')
    .replace(/\s+/g, ' ')

  // Capture text items with their canvas-pixel positions
  const vt = viewport.transform
  const applyVp = (x: number, y: number) => [
    vt[0] * x + vt[2] * y + vt[4],
    vt[1] * x + vt[3] * y + vt[5],
  ]
  const textItems: TextItemPos[] = []
  for (const item of textContent.items) {
    if (!('str' in item)) continue
    const it = item as { str: string; transform: number[]; width: number; height: number }
    const [x, y] = applyVp(it.transform[4], it.transform[5])
    textItems.push({
      text: it.str,
      x,
      y,
      width: it.width * RENDER_SCALE,
      height: it.height * RENDER_SCALE,
    })
  }

  // Extract map scale (e.g. "1:1000", "SCALE 1:500") from title block text
  const scaleMatch = text.match(/(?:SCALE\s*)?1\s*:\s*(\d{3,5})/i)
  const mapScale = scaleMatch ? parseInt(scaleMatch[1], 10) : null

  return {
    dataUrl: canvas.toDataURL('image/jpeg', 0.82),
    widthPx: viewport.width,
    heightPx: viewport.height,
    widthMm,
    heightMm,
    numPages: pdf.numPages,
    text,
    mapScale,
    textItems,
  }
}

async function geocodeFromText(text: string): Promise<[number, number] | null> {
  const candidates: string[] = []
  const streetRegex = /\d{1,5}\s+[A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+){0,3}\s+(?:St|Street|Rd|Road|Ave|Avenue|Ln|Lane|Dr|Drive|Ct|Court|Hwy|Highway|Pl|Place|Way|Tce|Terrace|Blvd|Boulevard|Cres|Crescent|Freeway|Fwy)[^,]*/gi
  candidates.push(...(text.match(streetRegex) || []).slice(0, 3))
  const suburbRegex = /[A-Z][a-zA-Z ]+,?\s+(?:VIC|NSW|QLD|WA|SA|TAS|ACT|NT)\s+\d{4}/g
  candidates.push(...(text.match(suburbRegex) || []).slice(0, 2))
  for (const q of candidates) {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`
      )
      const data = await res.json()
      if (data.length > 0) return [parseFloat(data[0].lat), parseFloat(data[0].lon)]
    } catch {
      /* ignore */
    }
  }
  return null
}

// ─── Transform math (similarity from ≥2 control points) ────────────────────

const REF_ZOOM = 20

function computeTransform(points: ControlPoint[], map: L.Map): Affine | null {
  if (points.length < 2) return null

  // Project each control point's latlng to pixel space at REF_ZOOM
  const projected = points.map((p) => {
    const pt = map.project([p.lat, p.lng], REF_ZOOM)
    return { src: p.pdf, dst: pt }
  })

  // Similarity transform: X = a*x - b*y + tx ; Y = b*x + a*y + ty
  // Least squares for 4 unknowns (a, b, tx, ty)
  // Stack equations: for each point (xi,yi)→(Xi,Yi):
  //   a*xi + b*(-yi) + tx + 0  = Xi
  //   a*yi + b*( xi) + 0  + ty = Yi
  // Normal equations (sum):
  // Only the means are taken from this pass. The raw second-moment sums that
  // used to be accumulated here (sxx, syy, sxX, syX, sxY, syY2) belonged to the
  // uncentered derivation the comments below work through and then abandon —
  // the solve is done from CENTERED coordinates in the second loop. They were
  // written and never read (#1586), so removing them changes no output; it only
  // stops six multiply-accumulates per point doing nothing.
  let sx = 0, sy = 0, sX = 0, sY = 0
  const n = projected.length
  for (const { src, dst } of projected) {
    sx += src.x
    sy += src.y
    sX += dst.x
    sY += dst.y
  }
  // The system (for similarity) decouples into two 2x2 systems after some algebra.
  // Using derivation:
  //   a = (n*(sxX+syY2) - sx*sX - sy*sY) / (n*(sxx+syy) - sx² - sy²)
  //   b = (n*(syX - sxY) + sx*sY - sy*sX) / (n*(sxx+syy) - sx² - sy²)
  //   Wait that's not right. Let me redo.
  //
  // Re-derivation: for each point i:
  //   Xi = a*xi - b*yi + tx
  //   Yi = b*xi + a*yi + ty
  // Sum over i:
  //   sX = a*sx - b*sy + n*tx         (1)
  //   sY = b*sx + a*sy + n*ty         (2)
  // Multiply eq Xi by xi and sum:
  //   sxX = a*sxx - b*sxy + tx*sx     (3)
  //   where sxy = Σ xi*yi
  // Multiply eq Yi by xi and sum:
  //   sxY = b*sxx + a*sxy + ty*sx     (4)
  // Multiply eq Xi by yi:
  //   syX = a*sxy - b*syy + tx*sy     (5)
  // Multiply eq Yi by yi:
  //   syY = b*sxy + a*syy + ty*sy     (6)
  //
  // From (1): tx = (sX - a*sx + b*sy)/n
  // From (2): ty = (sY - b*sx - a*sy)/n
  //
  // Sub into (3) and (4) → 2 equations in (a, b):
  // (3) - sx/n * (1):
  //   sxX - sx*sX/n = a*(sxx - sx²/n) - b*(sxy - sx*sy/n)
  // (4) - sx/n * (2):
  //   sxY - sx*sY/n = b*(sxx - sx²/n) + a*(sxy - sx*sy/n)
  //
  // Let A = sxx - sx²/n, B = sxy - sx*sy/n (centered cross)
  //     U = sxX - sx*sX/n, V = sxY - sx*sY/n
  // Then:
  //   a*A - b*B = U
  //   b*A + a*B = V
  // => [A -B; B A] [a; b] = [U; V]
  // det = A² + B²; a = (A*U + B*V)/det, b = (A*V - B*U)/det
  //
  // We also need to use (5), (6) for full least-squares, but for pure similarity
  // the centered form above is equivalent to LS if we use centered sums.
  // Let's just compute from centered coords directly.
  const mx = sx / n, my = sy / n, mX = sX / n, mY = sY / n
  let Axx = 0, Ayy = 0, Bxy = 0, U = 0, V = 0, W = 0, Z = 0
  for (const { src, dst } of projected) {
    const dx = src.x - mx
    const dy = src.y - my
    const dX = dst.x - mX
    const dY = dst.y - mY
    Axx += dx * dx
    Ayy += dy * dy
    Bxy += dx * dy
    U += dx * dX + dy * dY
    V += dx * dY - dy * dX
    W += dx * dX
    Z += dy * dY
  }
  // For similarity (scale+rotation), closed form:
  //   a = (Σ dx*dX + dy*dY) / (Σ dx² + dy²)
  //   b = (Σ dx*dY - dy*dX) / (Σ dx² + dy²)
  const denom = Axx + Ayy
  if (denom === 0) return null
  const a = U / denom
  const b = V / denom
  const tx = mX - a * mx + b * my
  const ty = mY - b * mx - a * my

  // Suppress unused-var warnings (kept for reference derivation)
  void Bxy; void W; void Z

  return { a, b: -b, c: tx, d: b, e: a, f: ty }
}

function pdfToProjected(t: Affine, p: PdfPoint): { x: number; y: number } {
  return {
    x: t.a * p.x + t.b * p.y + t.c,
    y: t.d * p.x + t.e * p.y + t.f,
  }
}

/** Compute an affine transform from the current screen-space imgBox position.
 *  This lets a locked overlay appear exactly where the screen-space sheet was. */
function computeTransformFromImgBox(
  pd: PageData,
  imgBox: { left: number; top: number; w: number; h: number },
  rotationDeg: number,
  map: L.Map,
): Affine {
  const cx = imgBox.left + imgBox.w / 2
  const cy = imgBox.top + imgBox.h / 2
  const θ = rotationDeg * (Math.PI / 180)
  const cos = Math.cos(θ)
  const sin = Math.sin(θ)
  // Rotate a container point around the imgBox centre (CSS convention: positive = CW in screen)
  const rotate = (px: number, py: number) => ({
    x: cx + (px - cx) * cos - (py - cy) * sin,
    y: cy + (px - cx) * sin + (py - cy) * cos,
  })
  // 3 reference corners: TL, TR, BL of the imgBox after rotation
  const ptTL = rotate(imgBox.left, imgBox.top)
  const ptTR = rotate(imgBox.left + imgBox.w, imgBox.top)
  const ptBL = rotate(imgBox.left, imgBox.top + imgBox.h)
  // Convert container pixels → projected coords at REF_ZOOM
  const toProj = (x: number, y: number) => {
    const ll = map.containerPointToLatLng(L.point(x, y))
    return map.project(ll, REF_ZOOM)
  }
  const P0 = toProj(ptTL.x, ptTL.y)
  const P1 = toProj(ptTR.x, ptTR.y)
  const P2 = toProj(ptBL.x, ptBL.y)
  const W = pd.widthPx
  const H = pd.heightPx
  return {
    a: (P1.x - P0.x) / W,
    b: (P2.x - P0.x) / H,
    c: P0.x,
    d: (P1.y - P0.y) / W,
    e: (P2.y - P0.y) / H,
    f: P0.y,
  }
}

// ─── Rotated PDF overlay (custom Leaflet layer via DOM) ─────────────────────

function RotatedPdfOverlay({
  imageUrl,
  pdfWidth,
  pdfHeight,
  transform,
  opacity,
}: {
  imageUrl: string
  pdfWidth: number
  pdfHeight: number
  transform: Affine | null
  opacity: number
}) {
  const map = useMap()
  const containerRef = useRef<HTMLDivElement | null>(null)

  // Create container div once per mount
  useEffect(() => {
    const div = document.createElement('div')
    div.style.position = 'absolute'
    div.style.top = '0'
    div.style.left = '0'
    div.style.transformOrigin = '0 0'
    div.style.pointerEvents = 'none'
    div.style.willChange = 'transform'
    div.style.width = pdfWidth + 'px'
    div.style.height = pdfHeight + 'px'
    const img = document.createElement('img')
    img.style.width = pdfWidth + 'px'
    img.style.height = pdfHeight + 'px'
    img.style.display = 'block'
    img.style.userSelect = 'none'
    img.draggable = false
    div.appendChild(img)
    containerRef.current = div
    const pane = map.getPane('overlayPane')
    pane?.appendChild(div)
    return () => {
      div.remove()
      containerRef.current = null
    }
  }, [map, pdfWidth, pdfHeight])

  // Update image src / opacity
  useEffect(() => {
    const div = containerRef.current
    if (!div) return
    const img = div.querySelector('img')
    if (img) {
      img.src = imageUrl
      img.style.opacity = String(opacity)
    }
  }, [imageUrl, opacity])

  // Update transform on map move/zoom
  useEffect(() => {
    const div = containerRef.current
    if (!div) return
    if (!transform) {
      div.style.display = 'none'
      return
    }
    div.style.display = 'block'

    const unprojectPdf = (px: number, py: number) => {
      const p = pdfToProjected(transform, { x: px, y: py })
      return map.unproject(L.point(p.x, p.y), REF_ZOOM)
    }
    const update = () => {
      const tl = map.latLngToLayerPoint(unprojectPdf(0, 0))
      const tr = map.latLngToLayerPoint(unprojectPdf(pdfWidth, 0))
      const bl = map.latLngToLayerPoint(unprojectPdf(0, pdfHeight))
      const A = (tr.x - tl.x) / pdfWidth
      const D = (tr.y - tl.y) / pdfWidth
      const B = (bl.x - tl.x) / pdfHeight
      const E = (bl.y - tl.y) / pdfHeight
      div.style.transform = `matrix(${A}, ${D}, ${B}, ${E}, ${tl.x}, ${tl.y})`
    }
    update()
    map.on('move zoom viewreset zoomanim', update)
    return () => {
      map.off('move zoom viewreset zoomanim', update)
    }
  }, [map, transform, pdfWidth, pdfHeight])

  return null
}

// ─── Map helpers ─────────────────────────────────────────────────────────────

function MapClickHandler({
  onClick,
  onRightClick,
}: {
  onClick: (lat: number, lng: number) => void
  onRightClick: (lat: number, lng: number) => void
}) {
  useMapEvents({
    click(e) {
      onClick(e.latlng.lat, e.latlng.lng)
    },
    contextmenu(e) {
      e.originalEvent.preventDefault()
      onRightClick(e.latlng.lat, e.latlng.lng)
    },
  })
  return null
}


function MapFlyTo({ target }: { target: [number, number] | null }) {
  const map = useMap()
  useEffect(() => {
    if (target) map.flyTo(target, 17, { duration: 1.0 })
  }, [target, map])
  return null
}

function ZoomTracker({ onChange }: { onChange: (z: number) => void }) {
  const map = useMap()
  useEffect(() => {
    const update = () => onChange(map.getZoom())
    update()
    map.on('zoomend', update)
    return () => { map.off('zoomend', update) }
  }, [map, onChange])
  return null
}

function MetersPerPixelTracker({ onChange }: { onChange: (mpp: number) => void }) {
  const map = useMap()
  useEffect(() => {
    const update = () => {
      // Meters per pixel at the map center for the current zoom
      const center = map.getCenter()
      const lat = center.lat
      // At the equator, 1 pixel = 156543.03 / 2^zoom meters.
      // Adjust for latitude with cos(lat).
      const mpp = (156543.03 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, map.getZoom())
      onChange(mpp)
    }
    update()
    map.on('zoomend moveend', update)
    return () => { map.off('zoomend moveend', update) }
  }, [map, onChange])
  return null
}


function numberedIcon(n: number, color = '#E07B5D') {
  return L.divIcon({
    className: 'numbered-marker',
    html: `<div style="
      width:32px;height:32px;border-radius:50%;
      background:${color};color:white;
      display:flex;align-items:center;justify-content:center;
      font-weight:600;font-size:13px;
      border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,.4);
    ">${n}</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  })
}

function pitDotIcon(color = '#E07B5D') {
  return L.divIcon({
    className: '',
    html: `<div style="
      width:10px;height:10px;border-radius:50%;
      background:${color};border:2px solid white;
      box-shadow:0 1px 4px rgba(0,0,0,.5);
    "></div>`,
    iconSize: [10, 10],
    iconAnchor: [5, 5],
  })
}

// ─── Sidebar Dropzone ─────────────────────────────────────────────────────────

function SidebarDropzone({ onFile, isUploading }: { onFile: (f: File) => void; isUploading?: boolean }) {
  const onDrop = useCallback(
    (files: File[]) => files[0] && onFile(files[0]),
    [onFile]
  )
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: false,
    disabled: isUploading,
  })
  return (
    <div
      {...getRootProps()}
      className={`border border-dashed rounded-lg px-3 py-3 text-center cursor-pointer transition-colors ${
        isDragActive ? 'border-primary bg-primary/10' : 'border-gray-700 hover:border-gray-600'
      } ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <input {...getInputProps()} />
      <p className="text-[11px] text-gray-400">
        {isUploading ? 'Uploading…' : isDragActive ? 'Drop PDF here' : 'Drop PDF or click to browse'}
      </p>
    </div>
  )
}

// ─── Main Page ───────────────────────────────────────────────────────────────

const DEFAULT_CENTER: [number, number] = [-37.8136, 144.9631]

// ─── Rails API integration ───────────────────────────────────────────────────

type RailsPit = { id: number; name: string; lat: number | string; lng: number | string; position: number }

function railsPitToRoutePoint(pit: RailsPit): RoutePoint {
  return { id: String(pit.id), pitId: pit.name, lat: Number(pit.lat), lng: Number(pit.lng) }
}

// ─── Main component ──────────────────────────────────────────────────────────

function parseInitialSheetSettings(json?: string): Map<number, SheetSettings> | undefined {
  if (!json) return undefined
  try {
    const obj = JSON.parse(json) as Record<string, SheetSettings>
    if (!obj || typeof obj !== 'object' || Object.keys(obj).length === 0) return undefined
    const map = new Map<number, SheetSettings>()
    for (const [k, v] of Object.entries(obj)) map.set(Number(k), v)
    return map
  } catch {
    return undefined
  }
}

export default function RouteManager({
  mode = 'map',
  mapManagerBaseUrl,
  routeManagerBaseUrl,
  initialPitAId,
  initialPitBId,
  pitsUrl,
  reorderUrl,
  csrfToken,
  embedded,
  pdfUploadUrl,
  sheetSettingsUrl,
  initialSheetSettings,
  currentPdfUrl,
  currentPdfName,
  segmentsUrl,
  workPackagesUrl,
  crewsUrl,
}: {
  mode?: 'map' | 'route'
  mapManagerBaseUrl?: string
  routeManagerBaseUrl?: string
  initialPitAId?: string
  initialPitBId?: string
  pitsUrl?: string
  reorderUrl?: string
  routeBaseUrl?: string
  csrfToken?: string
  embedded?: boolean
  pdfUploadUrl?: string
  sheetSettingsUrl?: string
  initialSheetSettings?: string
  currentPdfUrl?: string
  currentPdfName?: string
  segmentsUrl?: string
  workPackagesUrl?: string
  crewsUrl?: string
} = {}) {
  const [file, setFile] = useState<File | null>(null)
  const [activePdfName, setActivePdfName] = useState<string | null>(currentPdfName ?? null)
  const [isUploadingPdf, setIsUploadingPdf] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [pdfDoc, setPdfDoc] = useState<pdfjs.PDFDocumentProxy | null>(null)
  const [numPages, setNumPages] = useState(0)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageCache, setPageCache] = useState<Map<number, PageData>>(new Map())
  const [controlPointsByPage, setControlPointsByPage] = useState<Map<number, ControlPoint[]>>(new Map())
  const [pendingPdfPoint, setPendingPdfPoint] = useState<PdfPoint | null>(null)
  const [sheetSettings, setSheetSettings] = useState<Map<number, SheetSettings>>(new Map())
  const mapAreaRef = useRef<HTMLDivElement>(null)
  const [overlaySize, setOverlaySize] = useState({ w: 0, h: 0 })
  const [currentZoom, setCurrentZoom] = useState(13)
  // Was OSM plus three Google layers served from the undocumented
  // `mt{s}.google.com/vt` endpoint — outside Google's terms of service, on a
  // page publicly routed at www.groundops.app/spikes/route-manager. The Google
  // layers and the basemap switcher were removed in #1648.
  //
  // This spike keeps OSM only and deliberately does NOT adopt the keyed
  // provider stack that issue added: that lives in src/app, and putting a
  // credential into the marketing bundle for a spike page would create a second
  // key surface to restrict, rotate and leak.
  const basemap = 'osm' as const
  const [flyTarget, setFlyTarget] = useState<[number, number] | null>(null)
  const [points, setPoints] = useState<RoutePoint[]>([])

  // Load pits from Rails API on mount (when running embedded in Rails admin)
  useEffect(() => {
    if (!pitsUrl) return
    fetch(pitsUrl, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((pits: RailsPit[]) => setPoints(pits.map(railsPitToRoutePoint)))
      .catch(console.error)
   
  }, [pitsUrl])

  // Load a PDF from a URL, optionally restoring saved sheet settings
  const loadPdfFromUrl = useCallback(async (url: string, restoredSettings?: Map<number, SheetSettings>) => {
    try {
      const pdf = await pdfjs.getDocument({ url }).promise
      setPdfDoc(pdf)
      setNumPages(pdf.numPages)
      setPageNumber(1)
      setPageCache(new Map())
      setControlPointsByPage(new Map())
      setSheetSettings(restoredSettings ?? new Map())
      // If restoring saved settings that include an Overview sheet, re-enable auto-approx
      // so that locking the first Map sheet still triggers auto-positioning of the rest.
      const hasOverview = restoredSettings != null &&
        [...restoredSettings.values()].some(s => s.type === 'Overview')
      setAutoApproxEnabled(hasOverview)
      setAutoApproxDialogPage(null)
      geocodedPagesRef.current = new Set()
      // Eagerly render all locked pages so their map overlays are immediately visible
      // (visibility is purely opacity-driven — not click-to-cache driven)
      if (restoredSettings) {
        for (const [pageNum, ss] of restoredSettings.entries()) {
          if (ss.locked) {
            renderPage(pdf, pageNum).then((data) =>
              setPageCache((prev) => new Map(prev).set(pageNum, data)),
            )
          }
        }
      }
    } catch (e) {
      console.error('Failed to load PDF from URL:', e)
    }
   
  }, [])

  // On mount: restore PDF and sheet settings from server
  useEffect(() => {
    if (currentPdfUrl) loadPdfFromUrl(currentPdfUrl, parseInitialSheetSettings(initialSheetSettings))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Debounced auto-save of sheet settings whenever they change
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (!sheetSettingsUrl || sheetSettings.size === 0) return
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => {
      const obj: Record<string, unknown> = {}
      sheetSettings.forEach((v, k) => { obj[String(k)] = v })
      fetch(sheetSettingsUrl, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken || '' },
        body: JSON.stringify({ settings: obj }),
      }).catch(console.error)
    }, 800)
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current) }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheetSettings])

  // Upload PDF: POST multipart to Rails → Rails uploads to S3 → load from serve_pdf (same-origin, no CORS)
  const handlePdfFile = useCallback(async (f: File) => {
    if (!pdfUploadUrl) {
      // No server integration — load locally (standalone/dev mode)
      setFile(f)
      setActivePdfName(f.name)
      return
    }
    setIsUploadingPdf(true)
    try {
      const formData = new FormData()
      formData.append('file', f)
      const res = await fetch(pdfUploadUrl, {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken || '' },
        body: formData,
      })
      const { url, filename } = await res.json()
      await loadPdfFromUrl(url)
      setActivePdfName(filename)
    } catch (e) {
      console.error('PDF upload failed:', e)
      setFile(f)
      setActivePdfName(f.name)
    } finally {
      setIsUploadingPdf(false)
    }
   
  }, [pdfUploadUrl, csrfToken, loadPdfFromUrl])

  const [pendingPit, setPendingPit] = useState<{ lat: number; lng: number } | null>(null)
  const [pitIdDraft, setPitIdDraft] = useState('')
  const [mapRef, setMapRef] = useState<L.Map | null>(null)

  // Auto-open InlineRoutePanel when arriving on route_manager with pit params
  const autoOpenedRef = useRef(false)
  useEffect(() => {
    if (mode !== 'route' || !initialPitAId || !initialPitBId || points.length === 0 || autoOpenedRef.current) return
    const pitA = points.find((p) => p.id === initialPitAId)
    const pitB = points.find((p) => p.id === initialPitBId)
    if (!pitA || !pitB) return
    autoOpenedRef.current = true
    const idxA = points.indexOf(pitA)
    const idxB = points.indexOf(pitB)
    setActiveRoute({ pitA, pitB, distM: haversineM(pitA.lat, pitA.lng, pitB.lat, pitB.lng), labelA: String(idxA + 1), labelB: String(idxB + 1) })
  }, [points, mode, initialPitAId, initialPitBId])

  // Auto-zoom map when arriving on map_manager with pit params
  const autoZoomedRef = useRef(false)
  const suppressPdfFlyRef = useRef(false)
  useEffect(() => {
    if (mode !== 'map' || !initialPitAId || !initialPitBId || !mapRef || points.length === 0 || autoZoomedRef.current) return
    const pitA = points.find((p) => p.id === initialPitAId)
    const pitB = points.find((p) => p.id === initialPitBId)
    if (!pitA || !pitB) return
    autoZoomedRef.current = true
    suppressPdfFlyRef.current = true  // block the imminent PDF page fly-to
    mapRef.fitBounds([[pitA.lat, pitA.lng], [pitB.lat, pitB.lng]], { padding: [80, 80], maxZoom: 19, animate: false })
  }, [points, mapRef, mode, initialPitAId, initialPitBId])

  const [, setMetersPerPixel] = useState(0)
  const geocodedPagesRef = useRef<Set<number>>(new Set())
  const [activeTab, setActiveTab] = useState<'route' | 'sheets'>(mode === 'route' ? 'route' : 'sheets')
  const [activeRoute, setActiveRoute] = useState<{ pitA: RoutePoint; pitB: RoutePoint; distM: number; labelA: string; labelB: string } | null>(null)
  const [lastUsedDate, setLastUsedDate] = useState(() => new Date().toISOString().split('T')[0])
  const [autoApproxEnabled, setAutoApproxEnabled] = useState(false)
  const [autoApproxDialogPage, setAutoApproxDialogPage] = useState<number | null>(null)

  // Per-sheet state accessors (current page)
  const ss = sheetSettings.get(pageNumber) ?? DEFAULT_SHEET
  const { opacity, pdfScale, rotation, locked } = ss
  const updateSheet = (patch: Partial<SheetSettings>) =>
    setSheetSettings((prev) => {
      const next = new Map(prev)
      next.set(pageNumber, { ...(prev.get(pageNumber) ?? DEFAULT_SHEET), ...patch })
      return next
    })
  // Update any page's sheet settings (used by SheetManager)
  const updateSheetForPage = (pageNum: number, patch: Partial<SheetSettings>) =>
    setSheetSettings((prev) => {
      const next = new Map(prev)
      next.set(pageNum, { ...(prev.get(pageNum) ?? DEFAULT_SHEET), ...patch })
      return next
    })

  // Track map area size for computing PDF overlay position.
  useEffect(() => {
    if (!mapAreaRef.current) return
    const el = mapAreaRef.current
    const rect = el.getBoundingClientRect()
    setOverlaySize({ w: rect.width, h: rect.height })
    const ro = new ResizeObserver((entries) => {
      for (const e of entries)
        setOverlaySize({ w: e.contentRect.width, h: e.contentRect.height })
    })
    ro.observe(el)
    return () => ro.disconnect()
   
  }, [])

  // Load PDF document once (local file pick — URL loading uses loadPdfFromUrl)
  useEffect(() => {
    if (!file) return
    loadPdf(file).then((pdf) => {
      setPdfDoc(pdf)
      setNumPages(pdf.numPages)
      setPageNumber(1)
      setPageCache(new Map())
      setControlPointsByPage(new Map())
      setSheetSettings(new Map())
      setAutoApproxEnabled(false)
      setAutoApproxDialogPage(null)
      geocodedPagesRef.current = new Set()
    })
  }, [file])

  // Render current page if not cached
  useEffect(() => {
    if (!pdfDoc) return
    if (pageCache.has(pageNumber)) return
    renderPage(pdfDoc, pageNumber).then((data) => {
      setPageCache((prev) => new Map(prev).set(pageNumber, data))
    })
  }, [pdfDoc, pageNumber, pageCache])

  const currentPage = pageCache.get(pageNumber) || null

  // On page change: if the page is already locked, fly the map to its geo-centre.
  // Otherwise geocode once to get an approximate position.
  useEffect(() => {
    if (!currentPage) return
    // If we arrived via pit params, skip this first fly-to so the pit zoom wins
    if (suppressPdfFlyRef.current) {
      suppressPdfFlyRef.current = false
      return
    }
     
    const pageSs = sheetSettings.get(pageNumber) ?? DEFAULT_SHEET
    if (pageSs.locked && pageSs.lockedTransform) {
      // Page is geo-referenced — fly the map to the centre of the locked sheet
      const T = pageSs.lockedTransform
      const cx = T.a * currentPage.widthPx / 2 + T.b * currentPage.heightPx / 2 + T.c
      const cy = T.d * currentPage.widthPx / 2 + T.e * currentPage.heightPx / 2 + T.f
      // Use mapRef from closure; it's always current at effect-run time
      const map = mapRef
      if (map) {
        const ll = map.unproject(L.point(cx, cy), REF_ZOOM)
        setFlyTarget([ll.lat, ll.lng])
        map.flyTo(ll)
      }
      return
    }
    // Not locked — geocode once per page
    if (geocodedPagesRef.current.has(pageNumber)) return
    geocodedPagesRef.current.add(pageNumber)
    geocodeFromText(currentPage.text).then((coord) => {
      if (coord) setFlyTarget(coord)
    })
  }, [currentPage, pageNumber]) // intentionally omit sheetSettings/mapRef — read from closure

  // Compute transform for current page.
  // With ≥2 control points → similarity transform.
  // With <2 → default transform that fits the PDF roughly in the current map viewport
  // so the user can see it immediately and start aligning.
  const [defaultTransformSeed, setDefaultTransformSeed] = useState(0)
  useEffect(() => {
    // Re-seed default transform when flying to a new page
    setDefaultTransformSeed((s) => s + 1)
  }, [flyTarget, pageNumber])

  // Compute transforms for all locked pages (used by RotatedPdfOverlay instances)
  const allTransforms = useMemo(() => {
    if (!mapRef) return new Map<number, Affine | null>()
    const result = new Map<number, Affine | null>()
    for (const [pageNum, pageSs] of sheetSettings.entries()) {
      if (!pageSs.locked) continue
      const pd = pageCache.get(pageNum)
      if (!pd) continue
      const cps = controlPointsByPage.get(pageNum) || []
      if (cps.length >= 2) {
        // ≥2 control points → precise similarity transform
        result.set(pageNum, computeTransform(cps, mapRef))
      } else if (pageSs.lockedTransform !== undefined) {
        // Transform captured from screen-space position at lock time
        result.set(pageNum, pageSs.lockedTransform ?? null)
      } else {
        // Fallback: anchor to geocoded location at zoom 17
        const anchor = flyTarget || DEFAULT_CENTER
        const ZOOM_ANCHOR = 17
        const scaleAtAnchor = 600 / pd.widthPx
        const projScale = scaleAtAnchor * Math.pow(2, REF_ZOOM - ZOOM_ANCHOR)
        const centerProj = mapRef.project(L.latLng(anchor[0], anchor[1]), REF_ZOOM)
        result.set(pageNum, {
          a: projScale, b: 0, c: centerProj.x - (projScale * pd.widthPx) / 2,
          d: 0, e: projScale, f: centerProj.y - (projScale * pd.heightPx) / 2,
        })
      }
    }
    return result
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef, sheetSettings, pageCache, controlPointsByPage, flyTarget, defaultTransformSeed])

  // Reset pending when page changes
  useEffect(() => {
    setPendingPdfPoint(null)
  }, [pageNumber])

  // Compute the fitted position of the current PDF sheet in screen space.
  // Moved above the early return so toggleLock can capture it.
  const PAD_TOP = 48, PAD_BOTTOM = 20, PAD_X = 20
  const innerW = Math.max(0, overlaySize.w - PAD_X * 2)
  const innerH = Math.max(0, overlaySize.h - PAD_TOP - PAD_BOTTOM)
  let imgBox = { left: PAD_X, top: PAD_TOP, w: 0, h: 0 }
  if (currentPage && innerW > 0 && innerH > 0) {
    const fitScale = Math.min(innerW / currentPage.widthPx, innerH / currentPage.heightPx) * (pdfScale / 100)
    const w = currentPage.widthPx * fitScale
    const h = currentPage.heightPx * fitScale
    imgBox = { left: PAD_X + (innerW - w) / 2, top: PAD_TOP + (innerH - h) / 2, w, h }
  }

  // Auto-approximate positions for all remaining Map sheets by reading label positions off the overview.
  // The offset of each sheet from 001 is expressed in continuous "box-length" fractions derived from
  // the measured spacing between labels on the overview page.
  const autoApproxRemainingSheets = async (
    firstPageNum: number,
    firstTransform: Affine,
    firstPageData: PageData,
  ) => {
    // ── 1. Collect all Map pages in document order ────────────────────────────
    const mapPages: number[] = []
    for (let i = 1; i <= numPages; i++) {
      const t = (sheetSettings.get(i) ?? DEFAULT_SHEET).type ?? 'Map'
      if (t === 'Map') mapPages.push(i)
    }

    // ── 2. Find Overview page ─────────────────────────────────────────────────
    let overviewPageNum: number | null = null
    for (let i = 1; i <= numPages; i++) {
      if ((sheetSettings.get(i) ?? DEFAULT_SHEET).type === 'Overview') {
        overviewPageNum = i
        break
      }
    }

    // ── 3. Extract label positions from overview text items ───────────────────
    type LabelInfo = { pageNum: number; id: string; x: number; y: number }
    const foundLabels: LabelInfo[] = []
    let ovData: PageData | null = null

    if (overviewPageNum !== null && pdfDoc) {
      ovData = pageCache.get(overviewPageNum) ?? null
      if (!ovData) {
        console.log('[AutoApprox] Rendering overview page', overviewPageNum)
        ovData = await renderPage(pdfDoc, overviewPageNum)
        setPageCache((prev) => new Map(prev).set(overviewPageNum!, ovData!))
      }
      console.log(
        `[AutoApprox] Overview page ${overviewPageNum}: ${ovData.widthPx}×${ovData.heightPx}px`,
        `— ${ovData.textItems.length} text items`,
      )
      console.log(
        '[AutoApprox] Text items:',
        ovData.textItems.map((ti) => `"${ti.text}"@(${Math.round(ti.x)},${Math.round(ti.y)})`).join('  '),
      )

      for (let idx = 0; idx < mapPages.length; idx++) {
        const pageNum = mapPages[idx]
        const n = idx + 1
        const paddedId = String(n).padStart(3, '0')   // "001", "002" …
        const unpaddedId = String(n)                   // "1", "2" …

        // Search strategy: exact padded → word-boundary padded → exact unpadded ONLY if n≥10
        // (single-digit exact matches are too ambiguous — "1", "2" appear everywhere)
        const item =
          ovData.textItems.find((ti) => ti.text.trim() === paddedId) ??
          ovData.textItems.find((ti) => new RegExp(`\\b${paddedId}\\b`).test(ti.text.trim())) ??
          (n >= 10 ? ovData.textItems.find((ti) => ti.text.trim() === unpaddedId) : undefined)

        if (item) {
          foundLabels.push({ pageNum, id: paddedId, x: item.x, y: item.y })
          console.log(
            `[AutoApprox] ✓ "${paddedId}" matched text "${item.text.trim()}" → canvas (${Math.round(item.x)}, ${Math.round(item.y)})`,
          )
        } else {
          console.log(`[AutoApprox] ✗ "${paddedId}" not found on overview`)
        }
      }
    } else {
      console.log('[AutoApprox] No overview page set — falling back to sequential placement')
    }

    // ── 4. Determine geo-offset strategy ─────────────────────────────────────
    // PRIMARY: if the overview sheet is already locked we can apply its geo-transform
    // directly to the label pixel offset — this is the only unit-correct approach.
    // FALLBACK: derive a uniform spacing from the inter-label distances on the overview
    // and scale by the ratio of overview physical size to map sheet physical size.
    const overviewSs = overviewPageNum != null ? sheetSettings.get(overviewPageNum) : undefined
    const ovTransform = overviewSs?.lockedTransform ?? null

    if (ovTransform) {
      console.log('[AutoApprox] Overview is locked — using direct geo-transform approach ✓')
    } else {
      console.log('[AutoApprox] Overview NOT locked — using physical-size ratio fallback (lock overview first for best accuracy)')
    }

    // Fallback spacing: use ratio of overview physical width to map sheet physical width
    // to convert overview canvas-px offsets into "map-sheet-width" fractions.
    // overview canvas pixels : overview mm = map sheet canvas pixels : map sheet mm
    // → 1 overview px = (firstPageData.widthPx / firstPageData.widthMm) / (ovData?.widthPx / ovData?.widthMm) ... same dpi
    // so: fracX = dx_ovPx * (mapSheetMm / overviewMm) / mapSheetWidthPx ... simplifies to:
    //     fracX = dx_ovPx / overviewWidthPx * (overviewMm / mapSheetMm)
    let colScalePx = ovData ? ovData.widthPx : firstPageData.widthPx
    let rowScalePx = ovData ? ovData.heightPx : firstPageData.heightPx
    if (ovData && firstPageData.widthMm > 0 && firstPageData.heightMm > 0) {
      // Scale so that 1 unit = 1 actual map sheet width/height
      colScalePx = ovData.widthPx * firstPageData.widthMm / (ovData.widthMm || firstPageData.widthMm)
      rowScalePx = ovData.heightPx * firstPageData.heightMm / (ovData.heightMm || firstPageData.heightMm)
    }

    // Debug: show 001→002 offsets under both strategies
    const label001 = foundLabels.find((l) => l.pageNum === firstPageNum)
    const l002 = foundLabels.find((l) => l.id === '002')
    if (label001 && l002) {
      const dx = l002.x - label001.x
      const dy = l002.y - label001.y
      console.log(`[AutoApprox] 001→002 raw offset: (${Math.round(dx)}, ${Math.round(dy)}) px in overview canvas`)
      if (ovTransform) {
        const gx = ovTransform.a * dx + ovTransform.b * dy
        const gy = ovTransform.d * dx + ovTransform.e * dy
        console.log(`[AutoApprox] 001→002 via T_ov: geo offset (${gx.toFixed(1)}, ${gy.toFixed(1)}) proj-units`)
      } else {
        const fracX = dx / colScalePx
        const fracY = dy / rowScalePx
        console.log(`[AutoApprox] 001→002 fallback: (${fracX.toFixed(3)} box-widths, ${fracY.toFixed(3)} box-heights)  [colScale=${Math.round(colScalePx)}px rowScale=${Math.round(rowScalePx)}px]`)
      }
    }

    // ── 5. Compute transforms / mark failures ─────────────────────────────────
    const W = firstPageData.widthPx
    const H = firstPageData.heightPx
    const needsRender: number[] = []

    setSheetSettings((prev) => {
      const next = new Map(prev)
      mapPages.forEach((pageNum) => {
        if (pageNum === firstPageNum) return

        const lbl = foundLabels.find((l) => l.pageNum === pageNum)
        if (!lbl || !label001) {
          console.log(`[AutoApprox] Sheet doc-page ${pageNum}: no label found → No Approx`)
          next.set(pageNum, {
            ...(prev.get(pageNum) ?? DEFAULT_SHEET),
            autoApproxFailed: true,
            autoApprox: undefined,
            locked: false,
          })
          return
        }

        const dx = lbl.x - label001.x
        const dy = lbl.y - label001.y

        let approxTransform: Affine
        if (ovTransform) {
          // PRIMARY: apply overview geo-transform linearly to label offset
          // (translation cancels — we only need the linear part a,b,d,e)
          const gx = ovTransform.a * dx + ovTransform.b * dy
          const gy = ovTransform.d * dx + ovTransform.e * dy
          approxTransform = { ...firstTransform, c: firstTransform.c + gx, f: firstTransform.f + gy }
          console.log(`[AutoApprox] Sheet "${lbl.id}" via T_ov: geo Δ(${gx.toFixed(1)}, ${gy.toFixed(1)})`)
        } else {
          // FALLBACK: scale by physical-size ratio
          const fracX = dx / colScalePx
          const fracY = dy / rowScalePx
          approxTransform = {
            ...firstTransform,
            c: firstTransform.c + firstTransform.a * W * fracX + firstTransform.b * H * fracY,
            f: firstTransform.f + firstTransform.d * W * fracX + firstTransform.e * H * fracY,
          }
          console.log(`[AutoApprox] Sheet "${lbl.id}" fallback: (${fracX.toFixed(3)} bw, ${fracY.toFixed(3)} bh)`)
        }

        next.set(pageNum, {
          ...(prev.get(pageNum) ?? DEFAULT_SHEET),
          locked: true,
          lockedTransform: approxTransform,
          autoApprox: true,
          autoApproxFailed: undefined,
        })
        if (!pageCache.has(pageNum)) needsRender.push(pageNum)
      })
      return next
    })

    // Kick off rendering for pages not yet in the cache (overlays need image data)
    if (pdfDoc) {
      for (const pageNum of needsRender) {
        renderPage(pdfDoc, pageNum).then((data) =>
          setPageCache((prev) => new Map(prev).set(pageNum, data)),
        )
      }
    }
  }

  const toggleLock = () => {
    if (!locked && currentPage && mapRef && imgBox.w > 0) {
      // Capture the geo-referenced transform from the current screen-space position
      const lockedTransform = computeTransformFromImgBox(currentPage, imgBox, rotation, mapRef)
      updateSheet({ locked: true, lockedTransform })
      // If auto-approx is on and this is the first Map sheet, place all others
      if (autoApproxEnabled) {
        const mapPages: number[] = []
        for (let i = 1; i <= numPages; i++) {
          const t = (sheetSettings.get(i) ?? DEFAULT_SHEET).type ?? 'Map'
          if (t === 'Map') mapPages.push(i)
        }
        if (mapPages[0] === pageNumber && mapPages.length > 1) {
          void autoApproxRemainingSheets(pageNumber, lockedTransform, currentPage)
        }
      }
    } else {
      updateSheet({ locked: !locked, lockedTransform: undefined, autoApprox: undefined })
    }
  }

  const handleMapRightClick = (lat: number, lng: number) => {
    const suggested = nextPitId(points[points.length - 1]?.pitId)
    setPitIdDraft(suggested)
    setPendingPit({ lat, lng })
  }

  const confirmPit = async () => {
    if (!pendingPit || !pitIdDraft.trim()) return
    const { lat, lng } = pendingPit
    const name = pitIdDraft.trim()
    setPendingPit(null)
    setPitIdDraft('')
    if (pitsUrl) {
      try {
        const resp = await fetch(pitsUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken || '' },
          body: JSON.stringify({ pit: { name, lat, lng, position: points.length } }),
        })
        const created: RailsPit = await resp.json()
        setPoints((ps) => [...ps, railsPitToRoutePoint(created)])
      } catch (e) {
        console.error('Failed to create pit:', e)
      }
    } else {
      setPoints((ps) => [...ps, { id: crypto.randomUUID(), pitId: name, lat, lng }])
    }
  }

  const handleMapClick = (lat: number, lng: number) => {
    if (pendingPdfPoint) {
      const cp: ControlPoint = {
        id: crypto.randomUUID(),
        pdf: pendingPdfPoint,
        lat,
        lng,
      }
      setControlPointsByPage((prev) => {
        const next = new Map(prev)
        next.set(pageNumber, [...(next.get(pageNumber) || []), cp])
        return next
      })
      setPendingPdfPoint(null)
    }
  }

  const movePoint = async (id: string, lat: number, lng: number) => {
    setPoints((ps) => ps.map((p) => (p.id === id ? { ...p, lat, lng } : p)))
    if (pitsUrl) {
      try {
        await fetch(`${pitsUrl}/${id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken || '' },
          body: JSON.stringify({ pit: { lat, lng } }),
        })
      } catch (e) {
        console.error('Failed to update pit position:', e)
      }
    }
  }
  const removePoint = async (id: string) => {
    setPoints((ps) => ps.filter((p) => p.id !== id))
    if (pitsUrl) {
      try {
        await fetch(`${pitsUrl}/${id}`, {
          method: 'DELETE',
          headers: { 'X-CSRF-Token': csrfToken || '' },
        })
      } catch (e) {
        console.error('Failed to delete pit:', e)
      }
    }
  }
  const reorder = async (from: number, to: number) => {
    const next = [...points]
    const [m] = next.splice(from, 1)
    next.splice(to, 0, m)
    setPoints(next)
    if (reorderUrl) {
      try {
        await fetch(reorderUrl, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken || '' },
          body: JSON.stringify({ positions: next.map((p, i) => ({ id: parseInt(p.id), position: i })) }),
        })
      } catch (e) {
        console.error('Failed to reorder pits:', e)
      }
    }
  }

  return (
    <>
    <div className={`${embedded ? 'h-full w-full' : 'h-screen w-screen'} flex bg-surface-dark overflow-hidden`}>
      {/* ─── Map + overlay ─── */}
      <div
        ref={mapAreaRef}
        className="flex-1 min-w-0 relative overflow-hidden"
      >
        {/* Toolbar */}
        <div className="absolute top-3 left-3 right-3 z-[1000] flex items-center gap-3 pointer-events-none">
        </div>



        {/* Inline route panel — shown instead of map, map stays mounted */}
        {activeRoute && (
          <InlineRoutePanel
            key={activeRoute.pitA.id}
            pitA={activeRoute.pitA}
            pitB={activeRoute.pitB}
            distM={activeRoute.distM}
            labelA={activeRoute.labelA}
            labelB={activeRoute.labelB}
            onClose={() => setActiveRoute(null)}
            segmentsUrl={segmentsUrl}
            workPackagesUrl={workPackagesUrl}
            crewsUrl={crewsUrl}
            csrfToken={csrfToken}
            lastUsedDate={lastUsedDate}
            onDateUsed={setLastUsedDate}
          />
        )}

        <MapContainer
          center={DEFAULT_CENTER}
          zoom={13}
          maxZoom={24}
          style={{ width: '100%', height: '100%', display: activeRoute ? 'none' : 'block' }}
          className="z-0"
          ref={(m) => { if (m) setMapRef(m) }}
        >
          {basemap === 'osm' && (
            <TileLayer
              key="osm"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              maxNativeZoom={18}
              maxZoom={24}
            />
          )}
          <MapFlyTo target={flyTarget} />
          <MapClickHandler onClick={handleMapClick} onRightClick={handleMapRightClick} />
          <MetersPerPixelTracker onChange={setMetersPerPixel} />
          <ZoomTracker onChange={setCurrentZoom} />

          {Array.from(sheetSettings.entries()).map(([pageNum, pageSs]) => {
            if (!pageSs.locked) return null
            const pd = pageCache.get(pageNum)
            if (!pd) return null
            const t = allTransforms.get(pageNum) ?? null
            return (
              <RotatedPdfOverlay
                key={pageNum}
                imageUrl={pd.dataUrl}
                pdfWidth={pd.widthPx}
                pdfHeight={pd.heightPx}
                transform={t}
                opacity={pageSs.opacity}
              />
            )
          })}

          {points.map((p, i) => (
            <Marker
              key={p.id}
              position={[p.lat, p.lng]}
              icon={pitDotIcon()}
              draggable
              eventHandlers={{
                mouseover: (e) => e.target.setIcon(numberedIcon(i + 1)),
                mouseout: (e) => e.target.setIcon(pitDotIcon()),
                dragend: (e) => {
                  const ll = e.target.getLatLng()
                  movePoint(p.id, ll.lat, ll.lng)
                },
              }}
            />
          ))}
        </MapContainer>

        {/* Zoom indicator */}
        {!activeRoute && (
          <div className="absolute bottom-3 left-3 z-[1000] bg-surface/90 backdrop-blur rounded px-2 py-1 text-xs text-gray-300 font-mono pointer-events-none">
            z{currentZoom}
          </div>
        )}

        {/* Floating PDF sheet overlay — visible only when unlocked (for positioning) */}
        {currentPage && imgBox.w > 0 && !locked && (
          <div
            className="absolute pointer-events-none z-[500]"
            style={{
              left: imgBox.left,
              top: imgBox.top,
              width: imgBox.w,
              height: imgBox.h,
              transform: `rotate(${rotation}deg)`,
              transformOrigin: 'center center',
            }}
          >
            <img
              src={currentPage.dataUrl}
              alt={`PDF page ${pageNumber}`}
              style={{ width: '100%', height: '100%', opacity, display: 'block' }}
              draggable={false}
            />
          </div>
        )}
        {/* New pit dialog */}
        {pendingPit && (
          <div className="absolute inset-0 z-[2000] flex items-center justify-center bg-black/40">
            <div className="bg-surface border border-gray-700 rounded-xl shadow-2xl p-5 w-72">
              <h3 className="text-sm font-semibold text-white mb-1">Create new Pit</h3>
              <p className="text-xs text-gray-400 mb-3">Enter a pit ID for this location</p>
              <input
                autoFocus
                type="text"
                value={pitIdDraft}
                onChange={(e) => setPitIdDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') confirmPit()
                  if (e.key === 'Escape') setPendingPit(null)
                }}
                placeholder="e.g. LUM-MH-001"
                className="w-full px-3 py-2 text-sm text-white bg-surface-dark border border-gray-600 rounded-lg font-mono focus:outline-none focus:border-primary mb-4"
              />
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setPendingPit(null)}
                  className="px-3 py-1.5 text-xs text-gray-400 hover:text-white rounded-lg border border-gray-700 hover:border-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmPit}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-600 rounded-lg"
                >
                  Add Pit
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ─── Drawer ─── */}
      <div className="w-80 bg-surface border-l border-gray-800 flex flex-col">

        {/* Tab toggle */}
        <div className="flex border-b border-gray-800 shrink-0">
          {mode === 'map' && <button
            onClick={() => { setActiveTab('sheets'); setActiveRoute(null) }}
            className={`flex-1 px-3 py-2.5 text-xs font-medium transition-colors border-b-2 ${
              activeTab === 'sheets'
                ? 'text-white border-primary'
                : 'text-gray-500 border-transparent hover:text-gray-300'
            }`}
          >
            Sheets
          </button>}
          <button
            onClick={() => setActiveTab('route')}
            className={`flex-1 px-3 py-2.5 text-xs font-medium transition-colors border-b-2 ${
              activeTab === 'route'
                ? 'text-white border-primary'
                : 'text-gray-500 border-transparent hover:text-gray-300'
            }`}
          >
            Pits &amp; Routes
          </button>
        </div>

        {activeTab === 'route' && (
          <>
            <PitRouteLadder
              points={points}
              onReorder={reorder}
              onRemove={removePoint}
              onSelect={(p) => {
                setActiveRoute(null)
                setTimeout(() => mapRef?.flyTo([p.lat, p.lng], 19, { duration: 0.8 }), 30)
              }}
              onRouteOpen={(pitA, pitB) => {
                if (routeManagerBaseUrl) {
                  window.location.href = `${routeManagerBaseUrl}?pit_a_id=${pitA.id}&pit_b_id=${pitB.id}`
                } else {
                  setActiveRoute({ pitA, pitB, distM: haversineM(pitA.lat, pitA.lng, pitB.lat, pitB.lng), labelA: pitA.pitId, labelB: pitB.pitId })
                }
              }}
              onFocusRoute={(pitA, pitB) => {
                if (mapManagerBaseUrl) {
                  window.location.href = `${mapManagerBaseUrl}?pit_a_id=${pitA.id}&pit_b_id=${pitB.id}`
                } else {
                  setActiveRoute(null)
                  setTimeout(() => mapRef?.fitBounds([[pitA.lat, pitA.lng], [pitB.lat, pitB.lng]], { padding: [60, 60], maxZoom: 19, animate: true }), 30)
                }
              }}
            />
          </>
        )}

        {activeTab === 'sheets' && (
          <>
          {/* Active File section */}
          <div className="px-3 py-3 border-b border-gray-800 shrink-0">
            <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Active File</div>
            {activePdfName ? (
              <div className="flex items-center gap-2">
                <svg className="w-3.5 h-3.5 text-primary shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                </svg>
                <span className="text-xs text-gray-200 truncate flex-1 min-w-0" title={activePdfName}>{activePdfName}</span>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingPdf}
                  className="text-[10px] text-gray-500 hover:text-gray-300 shrink-0 disabled:opacity-40"
                >
                  Replace
                </button>
              </div>
            ) : (
              <SidebarDropzone onFile={handlePdfFile} isUploading={isUploadingPdf} />
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handlePdfFile(f); e.target.value = '' }}
            />
          </div>
          <SheetManager
            numPages={numPages}
            pageCache={pageCache}
            sheetSettings={sheetSettings}
            activePageNum={pageNumber}
            onOpacityChange={(pageNum, opacity) => updateSheetForPage(pageNum, { opacity })}
            onTypeChange={(pageNum, type) => {
              // Always reset lock when type changes — a re-typed sheet needs repositioning
              const patch: Partial<SheetSettings> = {
                type,
                locked: false,
                lockedTransform: undefined,
                autoApprox: undefined,
              }
              if (type === 'Title') patch.opacity = 0
              if (type === 'Overview') setAutoApproxDialogPage(pageNum)
              updateSheetForPage(pageNum, patch)
            }}
            onPageSelect={(pageNum) => setPageNumber(pageNum)}
            onSheetUpdate={updateSheetForPage}
            onLock={toggleLock}
            onUnlock={(pageNum) => updateSheetForPage(pageNum, { locked: false, lockedTransform: undefined, autoApprox: undefined })}
            autoApproxEnabled={autoApproxEnabled}
          />
          </>
        )}
      </div>
    </div>

    {/* Auto-approx dialog */}
    {autoApproxDialogPage !== null && (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60">
        <div className="bg-surface border border-gray-700 rounded-xl p-6 w-80 shadow-2xl">
          <h3 className="text-sm font-semibold text-white mb-2">Auto-approximate sheet positions?</h3>
          <p className="text-xs text-gray-400 mb-5 leading-relaxed">
            Lock the <span className="text-white font-medium">overview</span> sheet to geo-reference
            it, then lock <span className="text-white font-medium">sheet 001</span>. The remaining
            map sheets will be placed using label positions on the overview. You can refine each individually.
          </p>
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => { setAutoApproxEnabled(false); setAutoApproxDialogPage(null) }}
              className="px-4 py-1.5 text-xs text-gray-400 hover:text-white border border-gray-700 hover:border-gray-500 rounded-lg transition-colors"
            >
              No
            </button>
            <button
              onClick={() => {
                setAutoApproxEnabled(true)
                if (autoApproxDialogPage !== null) setPageNumber(autoApproxDialogPage)
                setAutoApproxDialogPage(null)
              }}
              className="px-4 py-1.5 text-xs text-white bg-primary hover:bg-primary/80 rounded-lg transition-colors"
            >
              Yes
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  )
}

// ─── Sheets & Routes ─────────────────────────────────────────────────────────

function SheetManager({
  numPages,
  pageCache,
  sheetSettings,
  activePageNum,
  onOpacityChange,
  onTypeChange,
  onPageSelect,
  onSheetUpdate,
  onLock,
  onUnlock,
  autoApproxEnabled,
}: {
  numPages: number
  pageCache: Map<number, PageData>
  sheetSettings: Map<number, SheetSettings>
  activePageNum: number
  onOpacityChange: (pageNum: number, opacity: number) => void
  onTypeChange: (pageNum: number, type: 'Map' | 'Overview' | 'Title') => void
  onPageSelect: (pageNum: number) => void
  onSheetUpdate: (pageNum: number, patch: Partial<SheetSettings>) => void
  onLock: () => void
  onUnlock: (pageNum: number) => void
  autoApproxEnabled: boolean
}) {
  const [lockPopupPage, setLockPopupPage] = useState<number | null>(null)

  if (numPages === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <p className="text-xs text-gray-500 text-center">Load a PDF to see sheets</p>
      </div>
    )
  }

  // Assign sequential IDs only to Map-type sheets
  const mapPages: number[] = []
  const mapIds = new Map<number, string>()
  let mapCount = 0
  for (let i = 1; i <= numPages; i++) {
    const t = (sheetSettings.get(i) ?? DEFAULT_SHEET).type ?? 'Map'
    if (t === 'Map') {
      mapPages.push(i)
      mapCount++
      mapIds.set(i, String(mapCount).padStart(3, '0'))
    }
  }
  const firstMapPage = mapPages[0]
  const firstMapLocked = firstMapPage != null && (sheetSettings.get(firstMapPage) ?? DEFAULT_SHEET).locked
  const showAutoApproxHint = autoApproxEnabled && !firstMapLocked

  return (
    <div className="flex-1 overflow-auto flex flex-col">
      {showAutoApproxHint && (
        <div className="shrink-0 px-3 py-2 bg-blue-500/10 border-b border-blue-500/20 text-[11px] text-blue-300 leading-relaxed">
          Lock the <span className="font-semibold">overview</span> sheet, then lock{' '}
          <span className="font-semibold">sheet 001</span> to auto-place all map sheets
        </div>
      )}
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="border-b border-gray-800">
            <th className="text-left px-2 py-2 text-[10px] uppercase tracking-wider text-gray-500 w-6">#</th>
            <th className="text-left px-2 py-2 text-[10px] uppercase tracking-wider text-gray-500 w-8">ID</th>
            <th className="text-left px-2 py-2 text-[10px] uppercase tracking-wider text-gray-500 w-[96px]">Type</th>
            <th className="text-left px-2 py-2 text-[10px] uppercase tracking-wider text-gray-500 w-[58px]">Status</th>
            <th className="text-center px-2 py-2 text-[10px] uppercase tracking-wider text-gray-500 w-8">Lock</th>
            <th className="text-left px-2 py-2 text-[10px] uppercase tracking-wider text-gray-500">Opacity</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNum) => {
            const ss = sheetSettings.get(pageNum) ?? DEFAULT_SHEET
            const type = ss.type ?? 'Map'
            const inCache = pageCache.has(pageNum)
            const id = type === 'Map' ? (mapIds.get(pageNum) ?? 'n/a') : 'n/a'
            const status = type === 'Title'
              ? 'Ignored'
              : ss.autoApproxFailed ? 'No Approx'
              : ss.locked && ss.autoApprox ? 'Auto Approx'
              : ss.locked ? 'Set'
              : type === 'Map' && inCache ? 'Auto Pos'
              : ''
            const clickableCell = 'px-2 py-1.5 font-mono cursor-pointer hover:text-primary transition-colors select-none'
            const showControls = lockPopupPage === pageNum && !ss.locked

            return (
              <>
                <tr key={pageNum} className={`border-b border-gray-800/40 hover:bg-white/[0.03] ${activePageNum === pageNum ? 'bg-white/[0.04]' : ''}`}>
                  <td
                    className={`${clickableCell} text-gray-400`}
                    onClick={() => onPageSelect(pageNum)}
                    title={`Go to sheet ${pageNum}`}
                  >
                    {pageNum}
                  </td>
                  <td
                    className={`${clickableCell} ${id === 'n/a' ? 'text-gray-600' : 'text-gray-400'}`}
                    onClick={() => onPageSelect(pageNum)}
                    title={`Go to sheet ${pageNum}`}
                  >
                    {id}
                  </td>
                  <td className="px-1.5 py-1">
                    <select
                      value={type}
                      onChange={(e) => onTypeChange(pageNum, e.target.value as 'Map' | 'Overview' | 'Title')}
                      className="w-full bg-surface-dark border border-gray-700 text-[11px] text-gray-300 rounded px-1 py-0.5 focus:outline-none focus:border-primary cursor-pointer"
                    >
                      <option value="Title">Title</option>
                      <option value="Overview">Overview</option>
                      <option value="Map">Map</option>
                    </select>
                  </td>
                  <td className="px-2 py-1.5">
                    {status && (
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap ${
                        status === 'Set'
                          ? 'bg-green-400/15 text-green-400'
                          : status === 'Auto Approx'
                          ? 'bg-blue-400/15 text-blue-400'
                          : status === 'Auto Pos'
                          ? 'bg-yellow-400/15 text-yellow-400'
                          : status === 'No Approx'
                          ? 'bg-orange-400/15 text-orange-400'
                          : 'bg-gray-700/50 text-gray-500'
                      }`}>
                        {status}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    {type === 'Title' ? null : ss.locked ? (
                      <button
                        title="Unlock sheet & adjust"
                        onClick={() => { onPageSelect(pageNum); onUnlock(pageNum); setLockPopupPage(pageNum) }}
                        className="text-base leading-none text-green-400 hover:text-orange-400 transition-colors"
                      >
                        🔒
                      </button>
                    ) : lockPopupPage === pageNum ? (
                      <button
                        title="Lock sheet in current position"
                        onClick={() => { onLock(); setLockPopupPage(null) }}
                        className="relative inline-flex items-center justify-center transition-colors group"
                      >
                        <span className="text-base leading-none text-orange-300 group-hover:text-orange-400 transition-colors">🔓</span>
                        <span className="absolute inset-0 flex items-center justify-center text-red-500 font-black text-sm pointer-events-none" style={{ textShadow: '0 0 3px white' }}>✕</span>
                      </button>
                    ) : sheetSettings.has(pageNum) ? (
                      <button
                        title="Set position & lock sheet"
                        onClick={() => { onPageSelect(pageNum); setLockPopupPage(pageNum) }}
                        className="text-base leading-none text-gray-500 hover:text-gray-400 transition-colors"
                      >
                        🔓
                      </button>
                    ) : (
                      <button
                        title="Set position & lock sheet"
                        onClick={() => { onPageSelect(pageNum); setLockPopupPage(pageNum) }}
                        className="text-[11px] leading-none text-gray-600 hover:text-gray-400 transition-colors select-none font-mono"
                      >
                        ○
                      </button>
                    )}
                  </td>

                  <td className="px-2 py-1.5">
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.05}
                      value={ss.opacity}
                      onChange={(e) => onOpacityChange(pageNum, parseFloat(e.target.value))}
                      className="w-full"
                    />
                  </td>
                </tr>

                {/* Inline controls row — shown when lock popup is open for this unlocked sheet */}
                {showControls && (
                  <tr key={`${pageNum}-controls`} className="border-b border-gray-800 bg-surface-dark">
                    <td colSpan={6} className="px-3 py-2.5">
                      <div className="flex flex-col gap-2">
                        {/* Size */}
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] text-gray-400 w-10 shrink-0">Size</span>
                          <input type="range" min={70} max={110} step={1} value={ss.pdfScale}
                            onChange={(e) => onSheetUpdate(pageNum, { pdfScale: parseInt(e.target.value, 10) })}
                            className="flex-1" />
                          <span className="text-[11px] text-gray-400 font-mono w-8 shrink-0">{ss.pdfScale}%</span>
                        </div>
                        {/* Rotation */}
                        <div className="flex items-center gap-1">
                          <span className="text-[11px] text-gray-400 w-10 shrink-0">Rotate</span>
                          <button onClick={() => onSheetUpdate(pageNum, { rotation: +(ss.rotation - 5).toFixed(2) })} className="px-1 text-[11px] text-gray-400 hover:text-white font-mono" title="−5°">⟪</button>
                          <button onClick={() => onSheetUpdate(pageNum, { rotation: +(ss.rotation - 1).toFixed(2) })} className="px-1 text-[11px] text-gray-400 hover:text-white font-mono" title="−1°">◀</button>
                          <button onClick={() => onSheetUpdate(pageNum, { rotation: +(ss.rotation - 0.1).toFixed(2) })} className="px-0.5 text-[10px] text-gray-400 hover:text-white font-mono" title="−0.1°">‹</button>
                          <input
                            type="number" value={ss.rotation} step={0.1}
                            onChange={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v)) onSheetUpdate(pageNum, { rotation: v }) }}
                            className="w-14 px-1 py-0.5 text-[11px] text-center text-white bg-black/30 border border-gray-700 rounded font-mono focus:outline-none focus:border-primary"
                          />
                          <span className="text-[10px] text-gray-500">°</span>
                          <button onClick={() => onSheetUpdate(pageNum, { rotation: +(ss.rotation + 0.1).toFixed(2) })} className="px-0.5 text-[10px] text-gray-400 hover:text-white font-mono" title="+0.1°">›</button>
                          <button onClick={() => onSheetUpdate(pageNum, { rotation: +(ss.rotation + 1).toFixed(2) })} className="px-1 text-[11px] text-gray-400 hover:text-white font-mono" title="+1°">▶</button>
                          <button onClick={() => onSheetUpdate(pageNum, { rotation: +(ss.rotation + 5).toFixed(2) })} className="px-1 text-[11px] text-gray-400 hover:text-white font-mono" title="+5°">⟫</button>
                          <button onClick={() => onSheetUpdate(pageNum, { rotation: 0 })} className="ml-0.5 px-1 text-[10px] text-gray-400 hover:text-white" title="Reset">⟲</button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ─── Inline Route Panel ───────────────────────────────────────────────────────

type Segment = {
  id: number
  leading_pit_id: number
  segment_type: string
  distance: number
  position: number
}

const SEGMENT_TYPE_LABELS: Record<string, string> = {
  trench: 'Trench',
  rock_drill: 'Rock Drill',
  otr: 'OTR',
}

const STATUS_COLORS: Record<string, string> = {
  complete:    '#16a34a',
  active:      '#22c55e',
  overdue:     '#f97316',
  not_started: '#d1d5db',
}

const SEGMENT_ORANGE    = '#f97316'              // orange — route planning segments and pits
const WP_SEG_BLUE      = '#2563eb'              // blue — enabled WP segments
const WP_SEG_BLUE_DIM  = 'rgba(37,99,235,0.15)' // light blue — disabled WP segments

/** Shared form for both adding and editing a segment */
function SegmentForm({
  title,
  initialType,
  initialDist,
  submitLabel,
  deleteLabel,
  onSubmit,
  onCancel,
  onDelete,
}: {
  title: string
  initialType: string
  initialDist: string
  submitLabel: string
  deleteLabel?: string
  onSubmit: (type: string, distanceM: number) => void
  onCancel: () => void
  onDelete?: () => void
}) {
  const [type, setType] = useState(initialType)
  const [dist, setDist] = useState(initialDist)

  return (
    <div className="flex flex-col items-center gap-3 bg-surface/90 border border-white/10 rounded-xl px-6 py-4 shadow-xl">
      <span className="text-xs text-gray-400 uppercase tracking-wider font-semibold">{title}</span>
      <div className="flex gap-2">
        {Object.entries(SEGMENT_TYPE_LABELS).map(([val, label]) => (
          <button
            key={val}
            onClick={() => setType(val)}
            className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${type === val ? 'bg-primary text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min="1"
          step="1"
          placeholder="Distance"
          value={dist}
          onChange={(e) => setDist(e.target.value)}
          className="w-28 px-3 py-1.5 rounded bg-white/10 border border-white/20 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-white/40"
        />
        <span className="text-xs text-gray-400">m</span>
      </div>
      <div className="flex gap-2">
        {onDelete && (
          <button
            onClick={onDelete}
            className="px-3 py-1.5 rounded text-xs text-red-400 hover:text-red-300 transition-colors"
          >
            {deleteLabel ?? 'Delete'}
          </button>
        )}
        <button
          onClick={onCancel}
          className="px-4 py-1.5 rounded text-xs text-gray-400 hover:text-white transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={() => {
            const d = parseFloat(dist)
            if (!isNaN(d) && d > 0) onSubmit(type, d)
          }}
          className="px-4 py-1.5 rounded bg-primary text-white text-xs font-semibold hover:opacity-90 transition-opacity"
        >
          {submitLabel}
        </button>
      </div>
    </div>
  )
}

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December']
const DAY_LABELS = ['Mo','Tu','We','Th','Fr','Sa','Su']

function formatDateDisplay(iso: string) {
  const [y, m, d] = iso.split('-')
  return `${d}/${m}/${y}`
}

function DatePicker({ value, onChange, defaultDate }: { value: string; onChange: (d: string) => void; defaultDate: string }) {
  const [open, setOpen] = useState(false)
  const [openAbove, setOpenAbove] = useState(false)
  const [viewYear, setViewYear] = useState(0)
  const [viewMonth, setViewMonth] = useState(0)
  const btnRef = useRef<HTMLButtonElement>(null)

  function openCalendar() {
    const base = value || defaultDate || new Date().toISOString().split('T')[0]
    const d = new Date(base + 'T00:00:00')
    setViewYear(d.getFullYear())
    setViewMonth(d.getMonth())
    // Flip upward if there isn't enough space below (~260px for the calendar)
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect()
      setOpenAbove(window.innerHeight - rect.bottom < 280)
    }
    setOpen(true)
  }

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate()
  const offset = (new Date(viewYear, viewMonth, 1).getDay() + 6) % 7 // Mon-first
  const today = new Date().toISOString().split('T')[0]

  function prevMonth() {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11) }
    else setViewMonth(m => m - 1)
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0) }
    else setViewMonth(m => m + 1)
  }

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={openCalendar}
        className={`px-3 py-1.5 rounded bg-white/10 border border-white/20 text-sm focus:outline-none focus:border-white/40 min-w-[120px] text-left transition-colors hover:bg-white/15 ${value ? 'text-white' : 'text-gray-500'}`}
      >
        {value ? formatDateDisplay(value) : 'Set date…'}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className={`absolute z-50 left-0 bg-gray-800 border border-white/20 rounded-xl shadow-2xl p-3 w-56 ${openAbove ? 'bottom-full mb-1' : 'top-full mt-1'}`}>
            <div className="flex items-center justify-between mb-2">
              <button onClick={prevMonth} className="p-1 rounded hover:bg-white/10 text-gray-300 text-lg leading-none">‹</button>
              <span className="text-xs text-white font-semibold">{MONTH_NAMES[viewMonth]} {viewYear}</span>
              <button onClick={nextMonth} className="p-1 rounded hover:bg-white/10 text-gray-300 text-lg leading-none">›</button>
            </div>
            <div className="grid grid-cols-7 mb-1">
              {DAY_LABELS.map(d => <div key={d} className="text-center text-[10px] text-gray-500 font-semibold py-0.5">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-y-0.5">
              {Array.from({ length: offset }).map((_, i) => <div key={`e${i}`} />)}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1
                const iso = `${viewYear}-${String(viewMonth + 1).padStart(2,'0')}-${String(day).padStart(2,'0')}`
                const isSelected = iso === value
                const isToday = iso === today
                return (
                  <button
                    key={day}
                    onClick={() => { onChange(iso); setOpen(false) }}
                    className={`text-[11px] rounded py-1 transition-colors font-medium
                      ${isSelected ? 'bg-primary text-white' : isToday ? 'border border-white/30 text-white hover:bg-white/10' : 'text-gray-300 hover:bg-white/10'}`}
                  >
                    {day}
                  </button>
                )
              })}
            </div>
            {value && (
              <button onClick={() => { onChange(''); setOpen(false) }} className="mt-2 text-[10px] text-gray-500 hover:text-red-400 w-full text-center transition-colors">
                Clear date
              </button>
            )}
          </div>
        </>
      )}
    </div>
  )
}

/** Edit form specifically for WP segments — includes date + AM/PM scheduling */
function WpSegmentEditForm({
  initialType,
  initialDist,
  initialScheduledOn,
  initialScheduledPeriod,
  defaultDate,
  onSubmit,
  onCancel,
  onDelete,
}: {
  initialType: string
  initialDist: string
  initialScheduledOn: string | null
  initialScheduledPeriod: string | null
  defaultDate: string
  onSubmit: (type: string, distanceM: number, scheduledOn: string | null, scheduledPeriod: string) => void
  onCancel: () => void
  onDelete?: () => void
}) {
  const [type, setType] = useState(initialType)
  const [dist, setDist] = useState(initialDist)
  const [scheduledOn, setScheduledOn] = useState(initialScheduledOn ?? '')
  const [period, setPeriod] = useState<'am' | 'pm'>((initialScheduledPeriod as 'am' | 'pm') ?? 'am')

  return (
    <div className="flex flex-col items-center gap-3 bg-surface/90 border border-white/10 rounded-xl px-6 py-4 shadow-xl">
      <span className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Edit Work Package Segment</span>

      {/* Type selector */}
      <div className="flex gap-2">
        {Object.entries(SEGMENT_TYPE_LABELS).map(([val, label]) => (
          <button
            key={val}
            onClick={() => setType(val)}
            className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${type === val ? 'bg-primary text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Distance */}
      <div className="flex items-center gap-2">
        <input
          type="number"
          min="1"
          step="1"
          placeholder="Distance"
          value={dist}
          onChange={(e) => setDist(e.target.value)}
          className="w-28 px-3 py-1.5 rounded bg-white/10 border border-white/20 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-white/40"
        />
        <span className="text-xs text-gray-400">m</span>
      </div>

      {/* Scheduled date + AM/PM */}
      <div className="flex items-center gap-2 w-full justify-center">
        <span className="text-xs text-gray-400 shrink-0">Scheduled</span>
        <DatePicker value={scheduledOn} onChange={setScheduledOn} defaultDate={defaultDate} />
        <div className="flex rounded overflow-hidden border border-white/20 text-xs font-semibold">
          <button
            onClick={() => setPeriod('am')}
            className={`px-3 py-1.5 transition-colors ${period === 'am' ? 'bg-primary text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`}
          >
            AM
          </button>
          <button
            onClick={() => setPeriod('pm')}
            className={`px-3 py-1.5 transition-colors ${period === 'pm' ? 'bg-primary text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`}
          >
            PM
          </button>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        {onDelete && (
          <button onClick={onDelete} className="px-3 py-1.5 rounded text-xs text-red-400 hover:text-red-300 transition-colors">
            Disable
          </button>
        )}
        <button onClick={onCancel} className="px-4 py-1.5 rounded text-xs text-gray-400 hover:text-white transition-colors">
          Cancel
        </button>
        <button
          onClick={() => {
            const d = parseFloat(dist)
            if (!isNaN(d) && d > 0) onSubmit(type, d, scheduledOn || null, period)
          }}
          className="px-4 py-1.5 rounded bg-primary text-white text-xs font-semibold hover:opacity-90 transition-opacity"
        >
          Save
        </button>
      </div>
    </div>
  )
}

type WpInfo = { id: number; title: string | null; current_status: string; enabled: boolean; span_id: number; leading_pit_id: number | null; work_crew_id: number | null; work_crew_name: string | null; earliest_scheduled_on: string | null }
type WpSegmentItem = {
  id: number
  work_package_id: number
  segment_id: number | null
  pit_id: number | null
  segment_type: string
  distance: number | null
  position: number
  enabled: boolean
  scheduled_on: string | null
  scheduled_period: string | null
  status: string
  session_progress: number
}

type CrewOption = { id: number; name: string }

function InlineRoutePanel({
  pitA,
  pitB,
  distM,
  labelA,
  labelB,
  onClose,
  segmentsUrl,
  workPackagesUrl,
  crewsUrl,
  csrfToken,
  lastUsedDate,
  onDateUsed,
}: {
  pitA: RoutePoint
  pitB: RoutePoint
  distM: number
  labelA: string
  labelB: string
  onClose: () => void
  segmentsUrl?: string
  workPackagesUrl?: string
  crewsUrl?: string
  csrfToken?: string
  lastUsedDate?: string
  onDateUsed?: (date: string) => void
}) {
  const [segments, setSegments] = useState<Segment[]>([])
  const [addingAt, setAddingAt] = useState<number | null>(null)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [wps, setWps] = useState<WpInfo[]>([])
  const [wpSegmentsMap, setWpSegmentsMap] = useState<Record<number, WpSegmentItem[]>>({})
  const [wpLoading, setWpLoading] = useState(false)
  const [wpEditingFor, setWpEditingFor] = useState<{ wpId: number; seg: WpSegmentItem } | null>(null)
  // afterSegId=null means insert before all middle segs; afterSegId=<id> means insert after that seg
  const [wpAddingFor, setWpAddingFor] = useState<{ wpId: number; afterSegId: number | null; defaultType: string; defaultDist: string } | null>(null)
  const [, setHoveredWpId] = useState<number | null>(null)
  const [wpMenuId, setWpMenuId] = useState<number | null>(null)
  const [crews, setCrews] = useState<CrewOption[]>([])
  const wpHoverLeaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [formAnchor, setFormAnchor] = useState<{ top: number; centerX: number } | null>(null)

  function captureAnchor(e: React.MouseEvent<HTMLElement>) {
    if (!containerRef.current) return
    const cr = containerRef.current.getBoundingClientRect()
    const er = e.currentTarget.getBoundingClientRect()
    setFormAnchor({ top: er.bottom - cr.top, centerX: er.left + er.width / 2 - cr.left })
  }

  const distLabel = distM >= 1000
    ? `${(distM / 1000).toFixed(2)} km`
    : `${Math.round(distM)} m`

  const dashedV = 'repeating-linear-gradient(to bottom, #6b7280 0 5px, transparent 5px 9px)'
  const dashedH = 'repeating-linear-gradient(to right, #6b7280 0 8px, transparent 8px 14px)'

  useEffect(() => {
    if (!segmentsUrl) return
    fetch(`${segmentsUrl}?leading_pit_id=${pitA.id}`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((data: Segment[]) => setSegments(data))
      .catch(() => {})
  }, [segmentsUrl, pitA.id])

  function closeAll() {
    setAddingAt(null)
    setEditingId(null)
    setWpEditingFor(null)
    setWpAddingFor(null)
  }

  function handleAdd(type: string, distanceM: number) {
    if (!segmentsUrl || !csrfToken) return
    setLoading(true)
    const insertIdx = addingAt ?? segments.length
    // Positions start at 2 (1 is reserved for the leading pit wp_segment)
    const dbPos = insertIdx + 2
    const shifted = segments.map((s) =>
      s.position >= dbPos ? { ...s, position: s.position + 1 } : s,
    )
    const patches = shifted
      .filter((s, i) => s.position !== segments[i]?.position)
      .map((s) =>
        fetch(`${segmentsUrl}/${s.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
          body: JSON.stringify({ segment: { position: s.position } }),
        }),
      )
    Promise.all(patches)
      .then(() =>
        fetch(segmentsUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
          body: JSON.stringify({ segment: { leading_pit_id: parseInt(pitA.id), segment_type: type, distance: distanceM, position: dbPos } }),
        }),
      )
      .then((r) => r.json())
      .then((created: Segment) => {
        setSegments([...shifted, created].sort((a, b) => a.position - b.position))
        closeAll()
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  function handleEdit(seg: Segment, type: string, distanceM: number) {
    if (!segmentsUrl || !csrfToken) return
    setLoading(true)
    fetch(`${segmentsUrl}/${seg.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ segment: { segment_type: type, distance: distanceM } }),
    })
      .then((r) => r.json())
      .then((updated: Segment) => {
        setSegments((prev) => prev.map((s) => s.id === updated.id ? updated : s))
        closeAll()
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  function handleDelete(seg: Segment) {
    if (!segmentsUrl || !csrfToken) return
    setLoading(true)
    fetch(`${segmentsUrl}/${seg.id}`, {
      method: 'DELETE',
      headers: { 'X-CSRF-Token': csrfToken },
    })
      .then(() => {
        setSegments((prev) => prev.filter((s) => s.id !== seg.id))
        closeAll()
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  const totalDist = segments.reduce((sum, s) => sum + s.distance, 0)
  const editingSeg = segments.find((s) => s.id === editingId) ?? null

  // ── Work Package ──────────────────────────────────────────────────────────

  // Load available crews once on open
  useEffect(() => {
    if (!crewsUrl) return
    fetch(crewsUrl, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((data: CrewOption[]) => setCrews(data))
      .catch(() => {})
  }, [crewsUrl])

  // Load all WPs + their segments for this pit pair on open
  useEffect(() => {
    if (!workPackagesUrl) return
    fetch(`${workPackagesUrl}?leading_pit_id=${pitA.id}`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then(async (data: WpInfo[]) => {
        setWps(data)
        const entries = await Promise.all(
          data.map(async (wp) => {
            const r = await fetch(`/admin/work_packages/${wp.id}/wp_segments`, { headers: { Accept: 'application/json' } })
            const segs: WpSegmentItem[] = await r.json()
            return [wp.id, segs] as [number, WpSegmentItem[]]
          })
        )
        setWpSegmentsMap(Object.fromEntries(entries))
      })
      .catch(() => {})
  }, [workPackagesUrl, pitA.id])

  async function handleCreateWp() {
    if (!workPackagesUrl || !csrfToken) return
    setWpLoading(true)
    try {
      const r = await fetch(workPackagesUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ work_package: {
        leading_pit_id: parseInt(pitA.id),
        trailing_pit_id: parseInt(pitB.id),
      }}),
      })
      if (!r.ok) { console.error('wp create failed:', await r.text()); return }
      const wp = await r.json() as WpInfo
      setWps((prev) => [...prev, wp])
      // Load the pre-created (disabled) wp_segments
      const segR = await fetch(`/admin/work_packages/${wp.id}/wp_segments`)
      const wpSegs = await segR.json() as WpSegmentItem[]
      setWpSegmentsMap((prev) => ({ ...prev, [wp.id]: wpSegs }))
    } finally {
      setWpLoading(false)
    }
  }

  async function handleToggleWpSegment(wpId: number, wpSegId: number, enabled: boolean) {
    if (!csrfToken) return
    setWpLoading(true)
    try {
      const r = await fetch(`/admin/work_packages/${wpId}/wp_segments/${wpSegId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ wp_segment: { enabled } }),
      })
      if (!r.ok) { console.error('wp_segment toggle failed:', await r.text()); return }
      const updated = await r.json() as WpSegmentItem
      setWpSegmentsMap((prev) => ({
        ...prev,
        [wpId]: (prev[wpId] ?? []).map((s) => s.id === updated.id ? updated : s),
      }))
    } finally {
      setWpLoading(false)
    }
  }

  function handleWpEdit(type: string, distanceM: number, scheduledOn: string | null, scheduledPeriod: string) {
    if (!csrfToken || !wpEditingFor) return
    const { wpId, seg } = wpEditingFor
    if (scheduledOn) onDateUsed?.(scheduledOn)
    setWpLoading(true)
    fetch(`/admin/work_packages/${wpId}/wp_segments/${seg.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ wp_segment: { segment_type: type, distance: distanceM, scheduled_on: scheduledOn, scheduled_period: scheduledPeriod } }),
    })
      .then((r) => r.json())
      .then((updated: WpSegmentItem) => {
        setWpSegmentsMap((prev) => {
          const newSegs = (prev[wpId] ?? []).map((s) => s.id === updated.id ? updated : s)
          // Recalculate earliest_scheduled_on for this WP from the updated segments
          const dates = newSegs.map((s) => s.scheduled_on).filter(Boolean) as string[]
          const earliest = dates.length > 0 ? dates.sort()[0] : null
          setWps((prevWps) => prevWps.map((w) => w.id === wpId ? { ...w, earliest_scheduled_on: earliest } : w))
          return { ...prev, [wpId]: newSegs }
        })
        setWpEditingFor(null)
      })
      .catch(() => {})
      .finally(() => setWpLoading(false))
  }

  function handleWpDisable() {
    if (!wpEditingFor) return
    const { wpId, seg } = wpEditingFor
    setWpEditingFor(null)
    handleToggleWpSegment(wpId, seg.id, false)
  }

  async function handleAddWpSeg(type: string, distanceM: number, scheduledOn: string | null, scheduledPeriod: string) {
    if (!csrfToken || !wpAddingFor) return
    const { wpId, afterSegId } = wpAddingFor
    if (scheduledOn) onDateUsed?.(scheduledOn)
    setWpLoading(true)
    try {
      const allSegs = (wpSegmentsMap[wpId] ?? []).slice().sort((a, b) => a.position - b.position)
      // Determine insert position: after the segment with afterSegId, or before all middle segs
      let dbPos: number
      if (afterSegId === null) {
        const firstMiddle = allSegs.find(s => !s.pit_id)
        dbPos = firstMiddle?.position ?? 2
      } else {
        const afterSeg = allSegs.find(s => s.id === afterSegId)
        dbPos = (afterSeg?.position ?? allSegs[allSegs.length - 1]?.position ?? 1) + 1
      }
      // Shift all existing segments at dbPos and above (including pit B)
      const toShift = allSegs.filter(s => s.position >= dbPos)
      await Promise.all(toShift.map(s =>
        fetch(`/admin/work_packages/${wpId}/wp_segments/${s.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
          body: JSON.stringify({ wp_segment: { position: s.position + 1 } }),
        })
      ))
      setWpSegmentsMap(prev => ({
        ...prev,
        [wpId]: (prev[wpId] ?? []).map(s => toShift.find(t => t.id === s.id) ? { ...s, position: s.position + 1 } : s),
      }))
      // Create the new segment at dbPos
      const body: Record<string, unknown> = {
        segment_type: type, position: dbPos, enabled: true,
        scheduled_on: scheduledOn ?? null,
        scheduled_period: scheduledPeriod,
      }
      if (distanceM > 0) body.distance = distanceM
      const r = await fetch(`/admin/work_packages/${wpId}/wp_segments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ wp_segment: body }),
      })
      if (!r.ok) { console.error('wp_segment create failed:', await r.text()); return }
      const created: WpSegmentItem = await r.json()
      setWpSegmentsMap(prev => ({ ...prev, [wpId]: [...(prev[wpId] ?? []), created] }))
      setWpAddingFor(null)
      if (scheduledOn) {
        setWps(prev => prev.map(w => w.id === wpId ? {
          ...w,
          earliest_scheduled_on: !w.earliest_scheduled_on || scheduledOn < w.earliest_scheduled_on ? scheduledOn : w.earliest_scheduled_on,
        } : w))
      }
    } finally {
      setWpLoading(false)
    }
  }

  async function handleAssignCrew(wpId: number, crewId: number | null) {
    if (!workPackagesUrl || !csrfToken) return
    const r = await fetch(`${workPackagesUrl}/${wpId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ work_package: { work_crew_id: crewId } }),
    })
    if (!r.ok) { console.error('crew assign failed:', await r.text()); return }
    const updated = await r.json() as WpInfo
    setWps((prev) => prev.map((w) => w.id === updated.id ? updated : w))
  }

  async function handleToggleWp(wpId: number, enabled: boolean) {
    if (!workPackagesUrl || !csrfToken) return
    setWpMenuId(null)
    const r = await fetch(`${workPackagesUrl}/${wpId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ work_package: { enabled } }),
    })
    if (!r.ok) { console.error('wp toggle failed:', await r.text()); return }
    const updated = await r.json() as WpInfo
    setWps((prev) => prev.map((w) => w.id === updated.id ? updated : w))
  }

  function ConnectorDot({ insertPos }: { insertPos: number }) {
    const active = addingAt === insertPos
    return (
      <button
        onClick={(e) => { if (!active) captureAnchor(e); setEditingId(null); setAddingAt(active ? null : insertPos) }}
        title="Add segment here"
        disabled={loading}
        className={`shrink-0 w-7 h-7 rounded-full border-2 transition-colors flex items-center justify-center font-bold text-sm leading-none
          ${active
            ? 'bg-primary border-primary text-white'
            : 'bg-white/10 border-white/30 text-gray-400 hover:bg-primary/60 hover:border-primary hover:text-white'
          }`}
      >
        +
      </button>
    )
  }

  /** Connector dot for WP rows — always visible, inserts after the given segment (or at start if afterSegId=null) */
  function WpConnectorDot({ wpId, afterSegId, defaultType, defaultDist }: {
    wpId: number; afterSegId: number | null; defaultType?: string; defaultDist?: string
  }) {
    const active = wpAddingFor?.wpId === wpId && wpAddingFor?.afterSegId === afterSegId
    return (
      <button
        onClick={(e) => {
          if (!active) captureAnchor(e)
          setWpEditingFor(null)
          setWpAddingFor(active ? null : { wpId, afterSegId, defaultType: defaultType ?? 'trench', defaultDist: defaultDist ?? '' })
        }}
        title="Add WP segment here"
        disabled={wpLoading}
        className={`shrink-0 w-7 h-7 rounded-full border-2 transition-colors flex items-center justify-center font-bold text-sm leading-none
          ${active
            ? 'bg-blue-600 border-blue-600 text-white'
            : 'bg-white/5 border-blue-400/40 text-blue-400 hover:bg-blue-600/60 hover:border-blue-500 hover:text-white'
          }`}
      >
        +
      </button>
    )
  }

  return (
    <div ref={containerRef} className="absolute inset-0 z-[900] bg-surface-dark flex flex-col select-none">
      {/* Header */}
      <div className="shrink-0 px-4 pt-3 pb-2">
        <button onClick={onClose} className="text-[11px] text-gray-400 hover:text-white transition-colors">
          ← Back to map
        </button>
      </div>

      {/* Vertically centered body */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

        {/* Top spacer — shrinks as table grows, keeping content centered */}
        <div className="flex-1" />

        {/* ══ TABLE — Row 1: route row ══ */}
        <div className="flex items-stretch">

          {/* Left: Pit A · segments · Pit B */}
          <div className="flex-1 flex items-center px-8 gap-0">

            {/* Pit A */}
            <div className="shrink-0 flex flex-col items-center gap-1.5">
              <div className="w-14 h-14 rounded-full bg-primary text-white flex items-center justify-center font-bold text-xl shadow-md">{labelA}</div>
              <span className="text-xs font-mono font-semibold text-white whitespace-nowrap">{pitA.pitId}</span>
            </div>

            {/* Segment zone */}
            <div className="flex-1 flex items-center min-w-0">
              {segments.length === 0 ? (
                <>
                  <div className="flex-1 h-px" style={{ background: dashedH }} />
                  <button
                    onClick={(e) => { captureAnchor(e); setAddingAt(0); setEditingId(null) }}
                    disabled={loading}
                    className="shrink-0 w-10 h-10 rounded-full border-2 border-white/30 bg-white/10 text-gray-300 hover:bg-primary/60 hover:border-primary hover:text-white transition-colors flex items-center justify-center font-bold text-xl"
                  >
                    +
                  </button>
                  <div className="flex-1 h-px" style={{ background: dashedH }} />
                </>
              ) : (
                <>
                  <ConnectorDot insertPos={0} />
                  {segments.map((seg, idx) => {
                    const flex = totalDist > 0 ? seg.distance / totalDist : 1
                    const isEditing = editingId === seg.id
                    return (
                      <Fragment key={seg.id}>
                        <div
                          className={`flex flex-col items-center justify-center h-14 rounded cursor-pointer transition-all text-white text-[10px] font-semibold shadow-md min-w-0 px-2 ${isEditing ? 'ring-2 ring-white/60 opacity-100' : 'opacity-90 hover:opacity-100'}`}
                          style={{ flex, backgroundColor: SEGMENT_ORANGE }}
                          onClick={(e) => { setAddingAt(null); if (!isEditing) captureAnchor(e); setEditingId(isEditing ? null : seg.id) }}
                          title="Click to edit"
                        >
                          <span className="truncate w-full text-center">{SEGMENT_TYPE_LABELS[seg.segment_type] ?? seg.segment_type}</span>
                          <span className="opacity-80">{seg.distance}m</span>
                        </div>
                        <ConnectorDot insertPos={idx + 1} />
                      </Fragment>
                    )
                  })}
                </>
              )}
            </div>

            {/* Pit B */}
            <div className="shrink-0 flex flex-col items-center gap-1.5">
              <div className="w-14 h-14 rounded-full bg-primary text-white flex items-center justify-center font-bold text-xl shadow-md">{labelB}</div>
              <span className="text-xs font-mono font-semibold text-white whitespace-nowrap">{pitB.pitId}</span>
            </div>

          </div>

          {/* Right: "Work Packages" label — aligned to bottom of pit ID text */}
          {workPackagesUrl && (
            <div className="w-56 shrink-0 border-l border-t border-r border-white/20 rounded-tr-lg flex items-end px-4 pb-1.5">
              <span className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Work Packages</span>
            </div>
          )}

        </div>


        {/* ══ TABLE — Row per WP ══ */}
        {workPackagesUrl && wps.map((wp) => {
          const wpSegs = wpSegmentsMap[wp.id] ?? []
          const isMenuOpen = wpMenuId === wp.id
          return (
            <Fragment key={wp.id}>
              {/* WP row */}
              <div
                className={`flex items-stretch pt-2 transition-opacity ${wp.enabled ? '' : 'opacity-40'}`}
                onMouseEnter={() => {
                  if (wpHoverLeaveTimer.current) clearTimeout(wpHoverLeaveTimer.current)
                  setHoveredWpId(wp.id)
                }}
                onMouseLeave={() => {
                  wpHoverLeaveTimer.current = setTimeout(() => setHoveredWpId(null), 80)
                }}
              >
                {/* Left: wp_segment cells — rendered from wpSegs directly, pits on ends */}
                <div className="flex-1 flex items-stretch px-8">
                  {(() => {
                    const pitASeg = wpSegs.find(s => s.pit_id === parseInt(pitA.id))
                    const pitBSeg = wpSegs.find(s => s.pit_id === parseInt(pitB.id))
                    const middleSegs = wpSegs.filter(s => !s.pit_id).sort((a, b) => a.position - b.position)
                    const totalMidDist = middleSegs.reduce((sum, s) => sum + (s.distance ?? 0), 0) || 1

                    /** Render a single WP segment as an enabled (editable) or disabled (click-to-enable) block */
                    const renderBlock = (seg: WpSegmentItem, label: string) => {
                      if (seg.enabled) {
                        const sc = STATUS_COLORS[seg.status] ?? STATUS_COLORS.not_started
                        const pp = Math.round((seg.session_progress ?? 0) * 100)
                        const isActiveEdit = wpEditingFor?.wpId === wp.id && wpEditingFor?.seg.id === seg.id
                        return (
                          <div
                            className={`w-full h-10 rounded cursor-pointer transition-all overflow-hidden flex flex-col border border-white/10 shadow-sm ${isActiveEdit ? 'ring-2 ring-white/60 opacity-100' : 'opacity-80 hover:opacity-100'}`}
                            onClick={(e) => { if (!isActiveEdit) captureAnchor(e); setWpAddingFor(null); setWpEditingFor(isActiveEdit ? null : { wpId: wp.id, seg }) }}
                            title="Click to edit"
                          >
                            <div className="h-[3px] shrink-0" style={{ backgroundColor: sc }} />
                            <div className="relative flex-1 flex flex-col items-center justify-center overflow-hidden px-1" style={{ backgroundColor: WP_SEG_BLUE }}>
                              {pp > 0 && <div className="absolute inset-0 bg-green-500 opacity-25" style={{ width: `${pp}%` }} />}
                              <span className="relative truncate w-full text-center text-white text-[10px] font-semibold">{label}</span>
                              {seg.distance != null && <span className="relative text-white text-[9px] opacity-70">{seg.distance}m</span>}
                            </div>
                          </div>
                        )
                      }
                      return (
                        <button
                          onClick={() => handleToggleWpSegment(wp.id, seg.id, true)}
                          disabled={wpLoading}
                          title={`Enable ${label}`}
                          className="w-full h-10 rounded border border-blue-400/40 transition-all flex flex-col items-center justify-center text-[10px] font-semibold overflow-hidden px-1 text-blue-300 hover:opacity-100"
                          style={{ backgroundColor: WP_SEG_BLUE_DIM }}
                        >
                          <span className="truncate">{label}</span>
                          {seg.distance != null && <span className="text-[9px] opacity-70">{seg.distance}m</span>}
                        </button>
                      )
                    }

                    return (
                      <>
                        {/* Pit A */}
                        <div className="w-14 shrink-0 flex items-center justify-center">
                          {pitASeg
                            ? renderBlock(pitASeg, 'Pit')
                            : <span className="text-[9px] text-gray-600">—</span>}
                        </div>

                        {/* Middle segments with connectors between each */}
                        <div className="flex-1 flex items-center min-w-0">
                          <WpConnectorDot wpId={wp.id} afterSegId={null} defaultType="trench" defaultDist="" />
                          {middleSegs.map((seg) => {
                            const flex = (seg.distance ?? 0) / totalMidDist
                            const label = SEGMENT_TYPE_LABELS[seg.segment_type] ?? seg.segment_type
                            return (
                              <Fragment key={seg.id}>
                                <div style={{ flex: Math.max(flex, 0.05) }} className="flex items-center px-0.5 min-w-0">
                                  {renderBlock(seg, label)}
                                </div>
                                <WpConnectorDot wpId={wp.id} afterSegId={seg.id} defaultType={seg.segment_type} defaultDist={String(seg.distance ?? '')} />
                              </Fragment>
                            )
                          })}
                          {middleSegs.length === 0 && <div className="flex-1" />}
                        </div>

                        {/* Pit B */}
                        <div className="w-14 shrink-0 flex items-center justify-center">
                          {pitBSeg
                            ? renderBlock(pitBSeg, 'Pit')
                            : <span className="text-[9px] text-gray-600">—</span>}
                        </div>
                      </>
                    )
                  })()}
                </div>

                {/* Right: WP details card */}
                <div className="w-56 shrink-0 border-l border-r border-white/20 px-3">
                  <div
                    className="relative rounded-lg bg-white/5 p-2 h-full cursor-pointer hover:bg-white/8 transition-colors"
                    onClick={() => setWpMenuId(isMenuOpen ? null : wp.id)}
                  >
                    {/* Inline menu */}
                    {isMenuOpen && (
                      <div className="absolute top-7 right-2 z-10 bg-gray-900 border border-white/20 rounded-lg shadow-xl p-2 flex flex-col gap-1 min-w-[150px]" onClick={(e) => e.stopPropagation()}>
                        {/* Crew assignment */}
                        {crews.length > 0 && (
                          <>
                            <div className="px-3 pt-1 pb-0.5 text-[10px] text-gray-500 uppercase tracking-wider">Assign Crew</div>
                            {crews.map((crew) => (
                              <button
                                key={crew.id}
                                onClick={() => { handleAssignCrew(wp.id, wp.work_crew_id === crew.id ? null : crew.id); setWpMenuId(null) }}
                                className={`px-3 py-1.5 rounded text-xs text-left transition-colors
                                  ${wp.work_crew_id === crew.id
                                    ? 'text-white bg-white/10'
                                    : 'text-gray-300 hover:text-white hover:bg-white/5'}`}
                              >
                                {crew.name}
                                {wp.work_crew_id === crew.id && <span className="ml-1 text-gray-500">✓</span>}
                              </button>
                            ))}
                            <div className="border-t border-white/10 my-1" />
                          </>
                        )}
                        {/* Enable/Disable */}
                        {wp.enabled ? (
                          <button
                            onClick={() => handleToggleWp(wp.id, false)}
                            className="px-3 py-1.5 rounded text-xs text-left text-red-400 hover:text-red-300 hover:bg-white/5 transition-colors"
                          >
                            Disable WP
                          </button>
                        ) : (
                          <button
                            onClick={() => handleToggleWp(wp.id, true)}
                            className="px-3 py-1.5 rounded text-xs text-left text-green-400 hover:text-green-300 hover:bg-white/5 transition-colors"
                          >
                            Enable WP
                          </button>
                        )}
                        <button
                          onClick={() => setWpMenuId(null)}
                          className="px-3 py-1.5 rounded text-xs text-left text-gray-500 hover:text-white hover:bg-white/5 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                    <div className="space-y-0.5 text-[11px]">
                      <div><span className="text-gray-600">Crew:</span>{' '}<span className={wp.work_crew_name ? 'text-gray-700 font-medium' : 'text-gray-400'}>{wp.work_crew_name ?? '—'}</span></div>
                      <div><span className="text-gray-600">Start:</span>{' '}<span className={wp.earliest_scheduled_on ? 'text-gray-700 font-medium' : 'text-gray-400'}>{wp.earliest_scheduled_on ? formatDateDisplay(wp.earliest_scheduled_on) : '—'}</span></div>
                      <div><span className="text-gray-600">Status:</span> <span className={`${wp.enabled ? 'text-gray-400' : 'text-orange-500'}`}>{wp.enabled ? '—' : 'Disabled'}</span></div>
                    </div>
                  </div>
                </div>
              </div>

            </Fragment>
          )
        })}

        {/* ══ TABLE — Last row: + New WP ══ */}
        {workPackagesUrl && (
          <div className="flex pt-2">
            <div className="flex-1" />
            <div className="w-56 shrink-0 border-l border-r border-b border-white/20 rounded-bl-lg rounded-br-lg px-3 pt-2 pb-3">
              <button
                onClick={handleCreateWp}
                disabled={wpLoading}
                className="px-3 py-1 rounded border border-white/20 text-gray-400 hover:border-primary/60 hover:text-white text-xs font-semibold transition-colors"
              >
                {wpLoading ? '…' : '+ New'}
              </button>
            </div>
          </div>
        )}

        {/* Drop lines + measure — left column only */}
        <div className="flex flex-1 min-h-[40px]">
          <div className="flex-1 flex flex-row px-8">
            <div className="w-14 flex flex-col items-center pt-2">
              <div className="flex-1 w-px" style={{ background: dashedV }} />
            </div>
            <div className="flex-1" />
            <div className="w-14 flex flex-col items-center pt-2">
              <div className="flex-1 w-px" style={{ background: dashedV }} />
            </div>
          </div>
          {workPackagesUrl && <div className="w-56 shrink-0" />}
        </div>
        <div className="flex">
          <div className="flex-1 px-8 pb-8 flex flex-col items-center gap-1.5">
            <div className="w-full h-px" style={{ background: dashedH }} />
            <span className="text-[10px] text-gray-500 uppercase tracking-wider">'Crow flies' Distance</span>
            <span className="text-2xl font-bold text-white font-mono">{distLabel}</span>
          </div>
          {workPackagesUrl && <div className="w-56 shrink-0" />}
        </div>

      </div>

      {/* Floating segment edit/add modal — anchored below clicked element */}
      {(addingAt !== null || editingSeg !== null || wpEditingFor !== null || wpAddingFor !== null) && (
        <>
          {/* Transparent backdrop for dismiss-on-click */}
          <div
            className="absolute inset-0 z-20"
            onClick={() => closeAll()}
          />
          <div
            className="absolute z-30 pointer-events-none"
            style={{
              top: formAnchor ? formAnchor.top + 8 : '50%',
              left: formAnchor ? formAnchor.centerX : '50%',
              transform: 'translateX(-50%)',
            }}
          >
          <div className="pointer-events-auto" onClick={(e) => e.stopPropagation()}>
            {addingAt !== null ? (
              <SegmentForm
                title="Add Segment"
                initialType="trench"
                initialDist=""
                submitLabel="Add"
                onSubmit={handleAdd}
                onCancel={closeAll}
              />
            ) : editingSeg ? (
              <SegmentForm
                title="Edit Segment"
                initialType={editingSeg.segment_type}
                initialDist={String(editingSeg.distance)}
                submitLabel="Save"
                onSubmit={(type, dist) => handleEdit(editingSeg, type, dist)}
                onCancel={closeAll}
                onDelete={wps.length === 0 ? () => handleDelete(editingSeg) : undefined}
              />
            ) : wpAddingFor !== null ? (
              <WpSegmentEditForm
                initialType={wpAddingFor.defaultType}
                initialDist={wpAddingFor.defaultDist}
                initialScheduledOn={null}
                initialScheduledPeriod={null}
                defaultDate={lastUsedDate ?? new Date().toISOString().split('T')[0]}
                onSubmit={handleAddWpSeg}
                onCancel={() => setWpAddingFor(null)}
              />
            ) : wpEditingFor ? (
              <WpSegmentEditForm
                initialType={wpEditingFor.seg.segment_type}
                initialDist={String(wpEditingFor.seg.distance ?? '')}
                initialScheduledOn={wpEditingFor.seg.scheduled_on ?? null}
                initialScheduledPeriod={wpEditingFor.seg.scheduled_period}
                defaultDate={lastUsedDate ?? new Date().toISOString().split('T')[0]}
                onSubmit={handleWpEdit}
                onCancel={() => setWpEditingFor(null)}
                onDelete={handleWpDisable}
              />
            ) : null}
          </div>
          </div>
        </>
      )}
    </div>
  )
}

// ─── Pit/route ladder ─────────────────────────────────────────────────────────

const PIT_H = 34   // height of each pit row
const ROUTE_H = 20 // height of each inter-pit route row

function PitRouteLadder({
  points,
  onReorder,
  onRemove,
  onSelect,
  onRouteOpen,
  onFocusRoute,
}: {
  points: RoutePoint[]
  onReorder: (from: number, to: number) => void
  onRemove: (id: string) => void
  onSelect: (p: RoutePoint) => void
  onRouteOpen?: (pitA: RoutePoint, pitB: RoutePoint) => void
  onFocusRoute?: (pitA: RoutePoint, pitB: RoutePoint) => void
}) {
  const [dragIdx, setDragIdx] = useState<number | null>(null)

  if (points.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-xs text-gray-500">Right-click the map to add pits</p>
      </div>
    )
  }

  const STEP = PIT_H + ROUTE_H  // 54px per pit+route pair
  // totalH: last pit starts at (n-1)*STEP, occupies PIT_H
  const totalH = (points.length - 1) * STEP + PIT_H

  return (
    <div className="flex-1 overflow-auto">
      {/* Column headers */}
      <div className="grid grid-cols-2 px-3 py-1.5 border-b border-gray-800 shrink-0 sticky top-0 bg-surface z-10">
        <div className="text-[10px] uppercase tracking-wider text-gray-500">Pit</div>
        <div className="text-[10px] uppercase tracking-wider text-gray-500">Route</div>
      </div>

      {/* Ladder body */}
      <div className="relative mx-2 my-1" style={{ height: totalH }}>
        {/* Vertical connector line — runs from centre of first pit to centre of last */}
        {points.length > 1 && (
          <div
            className="absolute w-0.5 bg-gray-700"
            style={{
              left: 11,
              top: PIT_H / 2,
              height: (points.length - 1) * STEP,
            }}
          />
        )}

        {points.map((p, i) => {
          const pitTop = i * STEP
          const nextP = points[i + 1]
          const dist = nextP ? haversineM(p.lat, p.lng, nextP.lat, nextP.lng) : null
          const routeTop = pitTop + PIT_H

          return (
            <div key={p.id}>
              {/* Pit row */}
              <div
                className={`absolute flex items-center gap-2 ${dragIdx === i ? 'opacity-40' : ''}`}
                style={{ top: pitTop, height: PIT_H, left: 0, width: '50%' }}
                draggable
                onDragStart={() => setDragIdx(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => {
                  if (dragIdx !== null && dragIdx !== i) onReorder(dragIdx, i)
                  setDragIdx(null)
                }}
              >
                <div
                  className="w-[22px] h-[22px] rounded-full bg-primary text-white flex items-center justify-center text-[10px] font-bold shrink-0 z-10 cursor-pointer hover:bg-primary/70 transition-colors"
                  onClick={() => onSelect(p)}
                  title="Zoom to pit"
                >
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-white font-mono truncate leading-tight">{p.pitId || '–'}</div>
                </div>
                <button
                  onClick={() => onRemove(p.id)}
                  className="text-gray-600 hover:text-red-400 text-[10px] pr-1"
                >
                  ✕
                </button>
              </div>

              {/* Route row — icons + distance between this pit and next */}
              {dist !== null && nextP && (
                <div
                  className="absolute flex items-center gap-2 pl-3"
                  style={{ top: routeTop, height: ROUTE_H, left: '50%', width: '50%' }}
                >
                  {/* Route icon — vertical squiggle with orange endpoints, opens route panel */}
                  {onRouteOpen && (
                    <button
                      onClick={() => onRouteOpen(p, nextP)}
                      title={`Edit route ${p.pitId} → ${nextP.pitId}`}
                      className="opacity-60 hover:opacity-100 transition-opacity shrink-0"
                    >
                      <svg width="14" height="18" viewBox="0 0 14 22" fill="none">
                        <circle cx="7" cy="2.5" r="2.5" fill="#E07B5D"/>
                        <path d="M7 5 C12 8, 2 12, 7 16" stroke="#E07B5D" strokeWidth="1.5" strokeLinecap="round"/>
                        <circle cx="7" cy="18.5" r="2.5" fill="#E07B5D"/>
                      </svg>
                    </button>
                  )}
                  {/* Map icon — folded map with orange fill, zooms to both pits */}
                  {onFocusRoute && (
                    <button
                      onClick={() => onFocusRoute(p, nextP)}
                      title={`Zoom map to ${p.pitId} – ${nextP.pitId}`}
                      className="opacity-60 hover:opacity-100 transition-opacity shrink-0"
                    >
                      <svg width="14" height="18" viewBox="0 0 20 22" fill="none">
                        <polygon points="1 5 1 19 7 16 13 19 19 16 19 2 13 5 7 2 1 5"
                                 fill="#E07B5D" fillOpacity="0.25" stroke="#E07B5D" strokeWidth="1.5" strokeLinejoin="round"/>
                        <line x1="7" y1="2" x2="7" y2="16" stroke="#E07B5D" strokeWidth="1" strokeOpacity="0.6"/>
                        <line x1="13" y1="5" x2="13" y2="19" stroke="#E07B5D" strokeWidth="1" strokeOpacity="0.6"/>
                      </svg>
                    </button>
                  )}
                  <span className="text-[10px] text-gray-500 font-mono leading-none">
                    {dist >= 1000 ? `${(dist / 1000).toFixed(2)}km` : `${Math.round(dist)}m`}
                  </span>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
